import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import apiClient from '../api/client';
import { toast } from "sonner";
import { Loader2, Camera, ArrowRight, ArrowLeft, Info, Home, Shield, AlertTriangle, Save, CheckCircle, MessageSquare, Trash2, RefreshCw, Mail, Clipboard, Share2, Printer, Clock, ListChecks, Download, Calendar, Phone } from "lucide-react";
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import AppMenu from '../components/AppMenu';
import AppFooter from '../components/AppFooter';
import {
  PROMO_30_KITCHEN,
} from '../lib/promoCopy';
import { toNumber } from '../lib/utils';
import { compressImageForUpload } from '../lib/compressImageForUpload';
import { getCategoryBySlug } from '../lib/categoryConfig';
import { CatIcon } from '../lib/iconMap';

// --- Shared Components ---
const ErrorMsg = ({ children }) => (
    <div className="text-red-500 text-sm mt-2 flex items-center gap-2 animate-in fade-in">
        <AlertTriangle className="h-4 w-4" /> {children}
    </div>
);

// --- Wizard Steps ---

// Step 1: Customer type + price model combined (was 2 separate steps)
const Step1_Basics = ({ data, update, next, goHome }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold text-primary">Kom igång</h2>
      <p className="text-muted-foreground text-sm mt-1">Välj vem projektet gäller och hur du vill få priset presenterat.</p>
    </div>

    <div>
      <Label as="p" className="mb-2 block font-semibold text-sm">Gäller arbetet dig privat eller ett företag?</Label>
      <div className="grid grid-cols-2 gap-3">
        {[
          { val: 'privat',  label: 'Privatperson', sub: 'Inkl. moms · ROT om villkoren uppfylls' },
          { val: 'foretag', label: 'Företag',       sub: 'Pris visas exkl. moms' },
        ].map(({ val, label, sub }) => (
          <button
            type="button"
            key={val}
            className={`border-2 rounded-xl p-4 cursor-pointer transition-all text-center w-full ` +
              (data.customer_type === val
                ? 'border-accent-dark bg-accent text-primary shadow-md ring-1 ring-accent-dark/30'
                : 'border-slate-200 bg-white hover:border-accent hover:bg-accent-light')}
            onClick={() => update('customer_type', val)}
            aria-pressed={data.customer_type === val}
          >
            <div className="font-bold text-base">{label}</div>
            <div className={`text-xs mt-0.5 ${data.customer_type === val ? 'text-primary/70' : 'text-muted-foreground'}`}>{sub}</div>
          </button>
        ))}
      </div>
    </div>

    <div>
      <Label as="p" className="mb-2 block font-semibold text-sm">Hur vill du ha priset?</Label>
      <div className="grid grid-cols-1 gap-2">
        {[
          { val: 'fastpris', label: 'Fastpris', sub: 'Preliminärt pris och spann utifrån dina uppgifter.' },
          { val: 'lopande',  label: 'Löpande',  sub: 'Preliminär uppskattning baserad på timpris.' },
        ].map(({ val, label, sub }) => (
          <button
            type="button"
            key={val}
            className={`border-2 rounded-xl px-4 py-3 cursor-pointer transition-all flex items-center gap-3 w-full text-left ` +
              (data.price_model === val
                ? 'border-accent-dark bg-accent text-slate-900 shadow-md ring-1 ring-accent-dark/30'
                : 'border-slate-200 bg-white hover:border-accent hover:bg-accent-light')}
            onClick={() => update('price_model', val)}
            aria-pressed={data.price_model === val}
          >
            <div
              className={`h-4 w-4 rounded-full border-2 shrink-0 ${
                data.price_model === val ? 'border-slate-900 bg-slate-900' : 'border-slate-400'
              }`}
              aria-hidden="true"
            />
            <div className="flex-1 min-w-0">
              <span className="font-bold text-sm">{label}</span>
              <p className={`text-xs mt-0.5 ${data.price_model === val ? 'text-slate-900/70' : 'text-muted-foreground'}`}>{sub}</p>
            </div>
          </button>
        ))}
      </div>
    </div>

    <div className="flex flex-col sm:flex-row gap-3 sm:justify-between mt-2">
      <Button variant="outline" onClick={goHome} className="w-full sm:w-auto"><Home className="mr-2 h-4 w-4" /> Start</Button>
      <Button onClick={next} size="lg" className="w-full sm:w-auto bg-[#374ef4] hover:bg-[#2a3fd4] text-white font-bold">
        Nästa <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  </div>
);

const Step3_Config = ({ data, update, next, back, errors, hideNav }) => {
  const Counter = ({ label, value, field, hint }) => (
    <div className="flex flex-col py-3 border-b border-slate-100 last:border-0">
      <div className="flex items-center justify-between mb-1">
          <Label className="font-medium">{label}</Label>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => update(field, Math.max(0, (value || 0) - 1))}>-</Button>
            <span className="w-8 text-center font-bold">{value || 0}</span>
            <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => update(field, (value || 0) + 1)}>+</Button>
          </div>
      </div>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </div>
  );

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-primary">Beskriv köket</h2>
      <p className="text-muted-foreground text-sm">Fyll i det du vet. Vi har förifyllt vanliga värden och du kan alltid justera.</p>
      
      <div className="space-y-6">
        <div>
            <h3 className="font-semibold text-lg mb-2">Skåp & Stommar</h3>
            <Card className="p-4">
            <Counter label="Antal underskåp" value={data.u_cabinets} field="u_cabinets" hint="Räkna stommar som står på golvet." />
            <Counter label="Antal väggskåp" value={data.v_cabinets} field="v_cabinets" hint="Räkna stommar som ska upp på väggen." />
            <Counter label="Antal högskåp" value={data.h_cabinets} field="h_cabinets" hint="Högskåp som kyl/frys/ugnsskåp." />
            </Card>
            {errors?.cabinets && <ErrorMsg>{errors.cabinets}</ErrorMsg>}
        </div>

        {/* Material Status Toggle */}
        <div>
            <Label className="mb-2 block font-semibold">Material & Leverans</Label>
            <RadioGroup
              value={data.material_status}
              onValueChange={(val) => update('material_status', val)}
              className="grid grid-cols-1 md:grid-cols-3 gap-2"
            >
                {[
                    { val: 'owned', label: 'Har redan köket', desc: 'Köket är beställt och klart att levereras.' },
                    { val: 'ordering', label: 'Ska beställa', desc: 'Du beställer själv, vi monterar.' },
                    { val: 'help', label: 'Vill ha hjälp', desc: 'Vi hjälper till med beställning & leverans.' },
                ].map((opt) => {
                    const isActive = data.material_status === opt.val;
                    return (
                        <div
                          key={opt.val}
                          className={
                            `border rounded-xl p-3 text-left cursor-pointer text-sm transition-all ` +
                            (isActive
                              ? 'border-accent-dark bg-accent text-primary shadow-md ring-1 ring-accent-dark/30'
                              : 'border-slate-200 bg-white hover:border-accent hover:bg-accent-light')
                          }
                          onClick={() => update('material_status', opt.val)}
                        >
                            <div className="font-semibold">{opt.label}</div>
                            <div className={isActive ? 'text-xs mt-1 text-primary/80' : 'text-xs mt-1 text-slate-600'}>
                                {opt.desc}
                            </div>
                        </div>
                    );
                })}
            </RadioGroup>
        </div>

        <div>
            <h3 className="font-semibold text-lg mb-2">Vitvaror</h3>
            <Card className="p-4">
            <Counter label="Integrerad diskmaskin" value={data.integrated_dishwasher} field="integrated_dishwasher" hint="Front och inpassning ingår." />
            <Counter label="Ugn / Micro" value={data.oven_micro} field="oven_micro" hint="Inpassning i skåp." />
            <Counter label="Integrerad kyl/frys" value={data.integrated_fridge_freezer} field="integrated_fridge_freezer" hint="Front/justering ingår." />
            <Counter label="Häll" value={data.hob} field="hob" hint="Mekanisk montering." />
            <Counter label="Fläkt" value={data.fan} field="fan" hint="Mekanisk montering." />
            </Card>
        </div>

        <h3 className="font-semibold text-lg mt-6">Övrigt</h3>
        <div className="grid gap-4">
          <div>
            <Label>Köksö</Label>
            <Select value={data.island} onValueChange={(val) => update('island', val)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ingen">Ingen</SelectItem>
                <SelectItem value="liten">Liten</SelectItem>
                <SelectItem value="stor">Stor</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground mt-1">Köksö kräver ofta mer inpassning.</div>
          </div>
          <div>
            <Label>Bänkskiva</Label>
            <Select value={data.worktop} onValueChange={(val) => update('worktop', val)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="laminat">Laminat / Trä</SelectItem>
                <SelectItem value="kompakt">Kompaktlaminat</SelectItem>
                <SelectItem value="sten">Sten / Komposit</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground mt-1">Välj material så kan vi räkna rätt tid.</div>
          </div>
           <div>
            <Label>Anpassningsnivå</Label>
            <Select value={data.panels} onValueChange={(val) => update('panels', val)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="lag">Enkel (få avslut och täcklister)</SelectItem>
                <SelectItem value="medel">Normal (vanligt kök)</SelectItem>
                <SelectItem value="hog">Komplex (många anpassningar, sneda väggar)</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground mt-1">Välj "Normal" om du är osäker.</div>
          </div>
          <div>
             <Label>Logistik</Label>
             <Select value={data.logistics} onValueChange={(val) => update('logistics', val)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bra">Bra (Nära parkering/Hiss)</SelectItem>
                <SelectItem value="normal">Normal (Trappor/Bärväg)</SelectItem>
                <SelectItem value="svar">Svår (Många trappor/Trångt)</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground mt-1">Hur lätt är det att bära in material?</div>
          </div>
        </div>
      </div>

      {!hideNav && (
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-between mt-6">
          <Button variant="outline" onClick={back} className="w-full sm:w-auto"><ArrowLeft className="mr-2 h-4 w-4" /> Tillbaka</Button>
          <Button onClick={next} className="w-full sm:w-auto bg-[#374ef4] hover:bg-[#2a3fd4] text-white">Nästa <ArrowRight className="ml-2 h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
};

const Step4_Addons = ({ data, update, next, back, hideNav }) => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold text-primary">Vill du lägga till fler arbeten?</h2>
    <p className="text-muted-foreground">Välj bara det du vill ha hjälp med. Om du inte väljer tillägg påverkar det inte din score.</p>
    
    <div className="space-y-4">
      {/* Golv - Radio buttons */}
      <Card className="p-4">
        <div className="font-bold mb-3">Är golvet lagt?</div>
        <RadioGroup 
          value={data.floor_laid ? 'ja' : 'nej'} 
          onValueChange={(v) => update('floor_laid', v === 'ja')}
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="ja" id="floor-ja" />
            <Label htmlFor="floor-ja">Ja</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="nej" id="floor-nej" />
            <Label htmlFor="floor-nej">Nej</Label>
          </div>
        </RadioGroup>
        <div className="text-xs text-muted-foreground mt-2">Om golvet inte är lagt använder vi distanser för sockeln.</div>
      </Card>

      {/* Rivning */}
      <Card className="p-4 flex items-center justify-between">
        <div>
            <div className="font-bold">Rivning av befintligt kök</div>
            <div className="text-sm text-muted-foreground">Gäller demontering och enklare rivning.</div>
        </div>
        <Checkbox checked={data.demolition} onCheckedChange={(val) => update('demolition', val)} />
      </Card>
      
      {/* Bortforsling med antal körningar */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div>
              <div className="font-bold">Bortforsling</div>
              <div className="text-sm text-muted-foreground">Gäller lastning/bortforsling till återvinning.</div>
          </div>
          <Checkbox checked={data.disposal} onCheckedChange={(val) => update('disposal', val)} />
        </div>
        {data.disposal && (
          <div className="mt-3">
            <Label className="text-sm">Antal körningar</Label>
            <Select value={String(data.disposal_runs || 1)} onValueChange={(v) => update('disposal_runs', parseInt(v))}>
              <SelectTrigger className="mt-1 w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 vända</SelectItem>
                <SelectItem value="2">2 vändor</SelectItem>
                <SelectItem value="3">3+ vändor</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </Card>

      {/* Målning */}
      <Card className="p-4">
        <div className="font-bold mb-3">Målning (spackel + målning)</div>
        <div className="grid grid-cols-2 gap-4">
            <div>
                <Label className="text-sm text-muted-foreground mb-1 block">Väggar (m²)</Label>
                <Input 
                    type="text" 
                    inputMode="decimal"
                    placeholder="0" 
                    value={data.painting_walls || ''} 
                    onChange={(e) => update('painting_walls', e.target.value)} 
                />
            </div>
            <div>
                <Label className="text-sm text-muted-foreground mb-1 block">Tak (m²)</Label>
                <Input 
                    type="text" 
                    inputMode="decimal"
                    placeholder="0" 
                    value={data.painting_ceiling || ''} 
                    onChange={(e) => update('painting_ceiling', e.target.value)} 
                />
            </div>
        </div>
      </Card>

      {/* Stänkskydd med väggtyp */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div>
              <div className="font-bold">Nytt stänkskydd/kakel</div>
              <div className="text-sm text-muted-foreground">Vi monterar nytt stänkskydd bakom bänken.</div>
          </div>
          <Checkbox checked={data.backsplash} onCheckedChange={(val) => update('backsplash', val)} />
        </div>
        {data.backsplash && (
          <div className="mt-3 space-y-3">
            <div>
              <Label className="text-sm">Längd (meter)</Label>
              <Input 
                type="text" 
                inputMode="decimal"
                placeholder="t.ex. 4" 
                className="mt-1 w-32"
                value={data.backsplash_length || ''} 
                onChange={(e) => update('backsplash_length', e.target.value)} 
              />
            </div>
            <div>
              <Label className="text-sm">Vägg bakom</Label>
              <Select value={data.backsplash_wall_type || 'vet_ej'} onValueChange={(v) => update('backsplash_wall_type', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="betong">Betong/tegel (enklare)</SelectItem>
                  <SelectItem value="gips">Gips (kräver extra arbete)</SelectItem>
                  <SelectItem value="vet_ej">Vet ej</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </Card>

      {/* El och VVS paket */}
      <div className="grid gap-4 mt-4">
        <div>
            <Label>El-paket</Label>
            <Select value={data.electrical} onValueChange={(val) => update('electrical', val)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ingen">Ingen el ingår</SelectItem>
                <SelectItem value="bas">Bas: Anslutning av vitvaror (5 600 kr)</SelectItem>
                <SelectItem value="medium">Medium: Bas + nya uttag vid bänk (11 400 kr)</SelectItem>
                <SelectItem value="plus">Plus: Medium + flytt av elcentral/ny grupp (17 100 kr)</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground mt-1">Utförs av behörig elektriker (underentreprenör).</div>
        </div>
        
        <div>
            <Label>VVS-paket</Label>
            <Select value={data.plumbing} onValueChange={(val) => update('plumbing', val)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ingen">Ingen VVS ingår</SelectItem>
                <SelectItem value="bas">Bas: Anslutning diskmaskin + blandare (4 500 kr)</SelectItem>
                <SelectItem value="flytt">Flytt: Bas + flytt av vatten/avlopp (10 200 kr)</SelectItem>
                <SelectItem value="plus">Plus: Flytt + ny rördragning/golvbrunn (14 800 kr)</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground mt-1">Utförs av behörig rörmokare (underentreprenör).</div>
        </div>
      </div>
    </div>

    {!hideNav && (
      <div className="flex flex-col sm:flex-row gap-3 sm:justify-between mt-6">
        <Button variant="outline" onClick={back} className="w-full sm:w-auto"><ArrowLeft className="mr-2 h-4 w-4" /> Tillbaka</Button>
        <Button onClick={next} className="w-full sm:w-auto bg-[#374ef4] hover:bg-[#2a3fd4] text-white">Nästa <ArrowRight className="ml-2 h-4 w-4" /></Button>
      </div>
    )}
  </div>
);

const Step5_Photos = ({ data, update, next, back, errors, hideNav }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    percent: 0,
    uploadedBytes: 0,
    totalBytes: 0,
    filesUploaded: 0,
    totalFiles: 0,
  });

  const loadedByIndexRef = React.useRef([]);
  const lastPercentRef = React.useRef(-1);

  const handleUpload = async (e) => {
    const inputEl = e?.target;
    const rawFiles = Array.from(e?.target?.files || []);
    if (!rawFiles.length || isUploading) return;

    setIsUploading(true);
    const prepToast = toast.loading("Komprimerar bilder (kan ta några sekunder)…");

    let files;
    try {
      files = await Promise.all(rawFiles.map((f) => compressImageForUpload(f)));
    } catch (prepErr) {
      toast.dismiss(prepToast);
      setIsUploading(false);
      if (inputEl) inputEl.value = "";
      const msg =
        prepErr?.code === "UPLOAD_TOO_LARGE"
          ? prepErr.message
          : prepErr?.message || "Kunde inte förbereda bilderna.";
      toast.error(msg);
      return;
    }

    toast.dismiss(prepToast);

    const totalBytes = files.reduce((sum, f) => sum + (f.size || 0), 0);
    loadedByIndexRef.current = new Array(files.length).fill(0);
    lastPercentRef.current = -1;

    setUploadProgress({
      percent: 0,
      uploadedBytes: 0,
      totalBytes,
      filesUploaded: 0,
      totalFiles: files.length,
    });

    const loadingToast = toast.loading("Laddar upp bilder…");

    // Upload i parallell (men med begränsad concurrency) för att inte göra
    // flera filer långsammare än nödvändigt i prod.
    const mapWithConcurrency = async (items, concurrency, fn) => {
      const results = new Array(items.length);
      let nextIndex = 0;

      const workers = new Array(concurrency).fill(0).map(async () => {
        while (true) {
          const current = nextIndex++;
          if (current >= items.length) return;
          results[current] = await fn(items[current], current);
        }
      });

      await Promise.all(workers);
      return results;
    };

    try {
        const concurrency = Math.min(4, files.length);
        const results = await mapWithConcurrency(files, concurrency, async (file, idx) => {
          const formData = new FormData();
          formData.append('file', file);

          const res = await apiClient.post('/upload', formData, {
            onUploadProgress: (evt) => {
              const loaded = evt?.loaded ?? 0;
              loadedByIndexRef.current[idx] = loaded;

              const uploadedBytes = loadedByIndexRef.current.reduce((a, b) => a + b, 0);
              const percent = totalBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : 0;

              // Undvik att spamma state updates för varje progress-event.
              if (percent !== lastPercentRef.current) {
                lastPercentRef.current = percent;
                setUploadProgress((prev) => ({
                  ...prev,
                  percent,
                  uploadedBytes,
                }));
              }
            },
          });

          // Markera filen som klar.
          loadedByIndexRef.current[idx] = file?.size || loadedByIndexRef.current[idx] || 0;
          const uploadedBytes = loadedByIndexRef.current.reduce((a, b) => a + b, 0);
          const percent =
            totalBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : uploadProgress.percent;

          setUploadProgress((prev) => ({
            ...prev,
            filesUploaded: prev.filesUploaded + 1,
            uploadedBytes,
            percent,
          }));

          return { filename: res.data.filename, url: res.data.url, provider: res.data.provider };
        });

        const uploaded = results.map(({ filename, url }) => ({ filename, url }));
        const provider = results[0]?.provider;
        update('photos', [...(data.photos || []), ...uploaded]);
        toast.dismiss(loadingToast);
        toast.success(
          provider ? `${files.length} bilder uppladdade (${provider})!` : `${files.length} bilder uppladdade!`
        );
    } catch (err) {
        toast.dismiss(loadingToast);
        const status = err?.response?.status;
        const apiError =
          err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.message ||
          "Okänt fel";
        const hint =
          status === 413 || /413|too large|payload|body/i.test(String(apiError))
            ? " Bilden är för stor för servern. Vi har redan optimerat, prova färre bilder åt gången."
            : "";
        toast.error(`Kunde inte ladda upp bilder. ${apiError}${hint}`);
    } finally {
      setIsUploading(false);
      if (inputEl) inputEl.value = "";
    }
  };

  const removePhoto = (index) => {
      const newPhotos = [...(data.photos || [])];
      newPhotos.splice(index, 1);
      update('photos', newPhotos);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-primary">Bilder (rekommenderas)</h2>
      <p className="text-muted-foreground">Bilder gör att vi kan ge ett smalare spann. Du kan få pris även utan bilder.</p>
      
      {/* Photo Guide */}
      <div className="grid grid-cols-2 gap-4 text-xs text-slate-600 mb-4 bg-slate-50 p-4 rounded border border-slate-100">
        <div>
            <strong className="block mb-1">Översikt</strong>
            Ta bilder från alla vinklar i rummet.
        </div>
        <div>
            <strong className="block mb-1">Detaljer</strong>
            Fota problemområden och befintliga installationer.
        </div>
      </div>
      
      <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:bg-slate-50 hover:border-accent transition-colors cursor-pointer">
        <Input
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          id="photo-upload"
          onChange={handleUpload}
          disabled={isUploading}
        />
        <Label htmlFor="photo-upload" className="cursor-pointer flex flex-col items-center">
            <Camera className="h-12 w-12 text-slate-400 mb-2" />
            <span className="font-semibold text-lg text-primary">Klicka för att ladda upp bilder</span>
            <span className="text-sm text-muted-foreground">
              JPG eller PNG · stora bilder komprimeras kraftigt innan uppladdning (tillräckligt för offert)
            </span>
        </Label>
      </div>

      {isUploading && (
        <div className="mt-4 bg-slate-50 border border-slate-100 rounded-xl p-4">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {uploadProgress.filesUploaded}/{uploadProgress.totalFiles} filer
            </span>
            <span>{uploadProgress.percent}%</span>
          </div>
          <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-accent rounded-full transition-[width] duration-150"
              style={{ width: `${uploadProgress.percent}%` }}
            />
          </div>
          <div className="mt-2 text-[11px] text-muted-foreground">
            {uploadProgress.totalBytes > 0
              ? `${Math.round(uploadProgress.uploadedBytes / 1024 / 1024)}MB / ${Math.round(
                  uploadProgress.totalBytes / 1024 / 1024
                )}MB`
              : "Laddar..."}
          </div>
        </div>
      )}

      {errors?.photos && <ErrorMsg>{errors.photos}</ErrorMsg>}

      <div className="grid grid-cols-3 gap-2 mt-4">
        {data.photos?.map((photo, idx) => (
            <div key={idx} className="relative aspect-square bg-slate-100 rounded overflow-hidden group">
                <img src={`${process.env.REACT_APP_BACKEND_URL || ''}${photo.url}`} alt="Uploaded" className="object-cover w-full h-full" />
                <Button 
                    variant="destructive" 
                    size="icon" 
                    className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                    disabled={isUploading}
                    onClick={() => removePhoto(idx)}
                >
                    <Trash2 className="h-3 w-3" />
                </Button>
            </div>
        ))}
      </div>

      {!hideNav && (
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-between mt-6">
          <Button variant="outline" onClick={back} className="w-full sm:w-auto"><ArrowLeft className="mr-2 h-4 w-4" /> Tillbaka</Button>
          <div className="flex gap-2">
              {(data.photos?.length || 0) === 0 && (
                  <Button variant="outline" onClick={next} disabled={isUploading} className="w-full sm:w-auto text-muted-foreground">
                      Hoppa över
                  </Button>
              )}
              {(data.photos?.length || 0) > 0 && (
                  <Button onClick={next} disabled={isUploading} className="w-full sm:w-auto bg-[#374ef4] hover:bg-[#2a3fd4] text-white">
                      Nästa <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

const Step6_Dimensions = ({ data, update, next, back, errors, hideNav }) => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-primary">Mått (rekommenderas)</h2>
      <p className="text-muted-foreground text-sm">Mått minskar prisspannet. Fyll i det du vet och lämna blankt annars.</p>
      {data.customer_type !== 'foretag' && <p className="text-sm font-semibold text-accent-dark">{PROMO_30_KITCHEN} räknas in i priset för privatperson.</p>}

      {/* Wall diagram */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
        <p className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Var är vägg A och B?</p>
        <div className="flex items-center justify-center">
          <svg viewBox="0 0 200 140" className="w-48 h-32 text-slate-600" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Room outline */}
            <rect x="30" y="20" width="140" height="100" rx="3" stroke="currentColor" strokeWidth="2.5" fill="#f8fafc" />
            {/* Wall A label - bottom */}
            <line x1="30" y1="130" x2="170" y2="130" stroke="#374ef4" strokeWidth="2" strokeDasharray="4 2"/>
            <text x="100" y="143" textAnchor="middle" fontSize="13" fontWeight="700" fill="#374ef4">A</text>
            {/* Wall B label - right */}
            <line x1="178" y1="20" x2="178" y2="120" stroke="#111111" strokeWidth="2" strokeDasharray="4 2"/>
            <text x="192" y="75" textAnchor="middle" fontSize="13" fontWeight="700" fill="#111111">B</text>
            {/* Dimension arrows A */}
            <text x="100" y="17" textAnchor="middle" fontSize="9" fill="#374ef4">längd A (cm)</text>
            {/* Dimension arrows B */}
            <text x="13" y="75" textAnchor="middle" fontSize="9" fill="#475569" transform="rotate(-90,13,75)">längd B (cm)</text>
          </svg>
        </div>
        <p className="text-xs text-slate-500 text-center mt-1">{data.category === 'kok' ? 'Mät längs väggarna där skåpen ska sitta.' : 'Mät rummets två väggar.'}</p>
      </div>

      <div className="space-y-4">
         <div className="grid grid-cols-2 gap-4">
            <div>
                <Label>Längd A (cm)</Label>
                <Input type="text" placeholder="t.ex. 320" value={data.dimensions?.wall_a || ''} onChange={(e) => update('dimensions', {...data.dimensions, wall_a: e.target.value})} />
            </div>
            <div>
                <Label>Längd B (cm)</Label>
                <Input type="text" placeholder="t.ex. 280" value={data.dimensions?.wall_b || ''} onChange={(e) => update('dimensions', {...data.dimensions, wall_b: e.target.value})} />
            </div>
         </div>
         <div>
            <Label>Takhöjd (cm)</Label>
            <Input type="text" placeholder="t.ex. 240" value={data.dimensions?.ceiling_height || ''} onChange={(e) => update('dimensions', {...data.dimensions, ceiling_height: e.target.value})} />
         </div>
         {errors?.dimensions && <ErrorMsg>{errors.dimensions}</ErrorMsg>}
         
         <div>
            <Label>Noteringar / övrigt</Label>
            <textarea 
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[100px]"
                value={data.dimensions?.notes} 
                onChange={(e) => update('dimensions', {...data.dimensions, notes: e.target.value})} 
                placeholder="t.ex. 'element under fönster', 'sneda väggar'"
            />
         </div>
      </div>
      
      {!hideNav && (
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-between mt-6 sm:items-end">
          <Button variant="outline" onClick={back} className="w-full sm:w-auto"><ArrowLeft className="mr-2 h-4 w-4" /> Tillbaka</Button>
          <div className="flex flex-col items-stretch sm:items-end gap-1 w-full sm:w-auto">
            <Button onClick={next} variant="default" className="w-full sm:w-auto bg-[#374ef4] hover:bg-[#2a3fd4] text-white font-bold px-8">Visa prisindikation</Button>
            <span className="text-[10px] text-muted-foreground text-center sm:text-right">{PROMO_30_KITCHEN} · privatperson</span>
          </div>
        </div>
      )}
    </div>
);

/** Måste matcha prefix i frontend/api/_lib/categoryLogic.ts (buildNonKitchenMissingItems). */
const MISSING_PREFIX_STEP2 = '__STEP2__ ';
const MISSING_PREFIX_STEP3 = '__STEP3__ ';

// Maps missing_items till guide-steg (kök: nyckelordslogik, övriga: STEP2/STEP3-prefix).
const getMissingByStep = (missingItems = [], category = 'kok') => {
    const isKitchen = category === 'kok';
    const stepLabels = {
        2: isKitchen ? 'Köksbeskrivning' : 'Beskriv arbetet & tillägg',
        3: isKitchen ? 'Bilder & Mått' : (category === 'el' ? 'Bilder' : 'Bilder & mått'),
    };
    const steps = {
        2: { label: stepLabels[2], items: [] },
        3: { label: stepLabels[3], items: [] },
    };

    missingItems.forEach((raw) => {
        if (typeof raw !== 'string') return;

        if (raw.startsWith(MISSING_PREFIX_STEP2)) {
            steps[2].items.push(raw.slice(MISSING_PREFIX_STEP2.length));
            return;
        }
        if (raw.startsWith(MISSING_PREFIX_STEP3)) {
            steps[3].items.push(raw.slice(MISSING_PREFIX_STEP3.length));
            return;
        }

        if (isKitchen) {
            const t = raw.toLowerCase();
            if (t.includes('mått') || t.includes('längd') || t.includes('takhöjd') || t.includes('bild') || t.includes('foto') || t.includes('ladda upp')) {
                steps[3].items.push(raw);
            } else {
                steps[2].items.push(raw);
            }
        } else {
            steps[2].items.push(raw);
        }
    });

    return Object.entries(steps)
        .filter(([, v]) => v.items.length > 0)
        .map(([stepNum, v]) => ({ step: Number(stepNum), ...v }));
};

/** Vägglängd krävs vid vägg/tapet-yta, eller om snabbvalet var vägg / vägg+tak / tapetsera (ej enbart tak). */
function malningNeedsWallLength(data) {
  const n = (f) => parseFloat(String(data[f] ?? '0').replace(',', '.')) || 0;
  if (n('painting_walls') > 0 || n('wallpaper_area') > 0) return true;
  const qt = data.malning_qe_type;
  return !!(qt && ['vagg', 'bagg_tak', 'tapetsera'].includes(qt));
}

// Returns true when sufficient data is present to show a price for the given category.
function isResultPriceAllowed(category, data) {
  const n = (f) => parseFloat(String(data[f] ?? '0').replace(',', '.')) || 0;
  switch (category) {
    case 'kok': return (data.u_cabinets || 0) + (data.v_cabinets || 0) + (data.h_cabinets || 0) > 0;
    case 'malning': {
      if (n('painting_walls') + n('painting_ceiling') + n('wallpaper_area') + n('strip_wallpaper_area') <= 0) return false;
      if (malningNeedsWallLength(data) && n('paint_wall_length_m') <= 0) return false;
      return true;
    }
    case 'vatrum': return !!data.wet_scope && !!data.wet_size && n('wet_area') > 0
      && n('wet_length_cm') > 0 && n('wet_width_cm') > 0;
    case 'kakel': return n('tile_wall_area') + n('tile_floor_area') > 0 && !!data.tile_type;
    case 'golv': {
      if (n('floor_area') <= 0) return false;
      if (data.floor_type === 'sand') {
        const f = data.floor_finish;
        if (!f || f === 'none') return false;
      }
      return true;
    }
    case 'vvs': {
      if (!data.vvs_job_type || !data.extra_notes?.trim()) return false;
      if (data.vvs_job_type === 'byte' && !data.vvs_replace_mixer && !data.vvs_replace_toilet) return false;
      return true;
    }
    case 'el': return !!data.el_job_type && !!data.el_job_size;
    case 'snickeri': {
      const hasWork = n('partition_length') + n('ceiling_area') + n('trim_length') +
        n('interior_doors') + n('exterior_doors') + n('casing_doors') > 0;
      return hasWork || (data.remodel_size && data.remodel_size !== 'none');
    }
    default: return true;
  }
}

// Returns a human-readable reason why the price is not shown yet.
function missingPriceReason(category) {
  switch (category) {
    case 'malning': return 'Ange ytor och vid vägg/tapet även längd på väggar för att se pris.';
    case 'vatrum': return 'Välj typ av renovering, storlek och ange yta samt mått för att se pris.';
    case 'kakel': return 'Ange kakelyta (vägg eller golv) och välj kakeltyp för att se pris.';
    case 'golv': return 'Ange golvyta (m²) för att se pris.';
    case 'vvs': return 'Välj typ av VVS-arbete och ange mer information för att se pris.';
    case 'el': return 'Välj typ av elarbete och storlek för att se pris.';
    case 'snickeri': return 'Fyll i minst ett arbetsmoment eller välj en ombyggnad för att se pris.';
    default: return 'Fyll i mer information för att se pris.';
  }
}

const Step7_Result = ({ data, result, onBook, back, goToStep, category, contactForm, priceRevealed, submittedCaseNumber, onContinueToThanks }) => {
    const bookingUrl = process.env.REACT_APP_BOOKING_URL || null;
    const catTitle = getCategoryBySlug(category)?.label || 'Projekt';

    if (!result) return (
        <div className="text-center p-8 space-y-4">
            <Loader2 className="h-8 w-8 animate-spin mx-auto" />
            <div>Beräknar...</div>
            <Button variant="ghost" onClick={back}>Avbryt</Button>
        </div>
    );

    const slug = category || 'kok';
    const isKitchenCat = slug === 'kok';
    const totalCabs = (data.u_cabinets || 0) + (data.v_cabinets || 0) + (data.h_cabinets || 0);
    const isSpecialProject = isKitchenCat && totalCabs === 0;
    const priceAllowed = isResultPriceAllowed(category, data);
    const priceRec = result.price_rec || 0;
    const priceLow = result.price_low || 0;
    const priceHigh = result.price_high || 0;
    const rotDeduction = result.rot_deduction || 0;
    const displayPrice = data.customer_type === 'privat' && rotDeduction > 0
        ? Math.round(priceRec - rotDeduction)
        : Math.round(priceRec);
    const privatAfterRot = data.customer_type === 'privat' && rotDeduction > 0;
    const displayPriceLow = privatAfterRot
        ? Math.max(0, Math.round(priceLow - rotDeduction))
        : Math.round(priceLow);
    const displayPriceHigh = privatAfterRot
        ? Math.round(priceHigh - rotDeduction)
        : Math.round(priceHigh);
    const isFixed = data.price_model === 'fastpris';
    const spanPct = Math.round((result.risk_factor || 0.25) * 100);
    const minDays = Math.ceil((result.hours_median || 16) / 8);
    const maxDays = Math.ceil((result.hours_p75 || 20) / 8);
    const daysText = minDays === maxDays ? `${minDays} dagar` : `${minDays}-${maxDays} dagar`;

    if (isSpecialProject || !priceAllowed) {
        const reason = isSpecialProject
            ? 'Fyll i antal skåp/stommar (minst 1) så kan vi räkna ut ett pris.'
            : missingPriceReason(category);
        return (
            <div className="space-y-6 animate-in fade-in duration-500">
                <Card className="border-2 border-slate-200 p-8 text-center space-y-4">
                    <MessageSquare className="h-12 w-12 mx-auto text-slate-400" />
                    <h3 className="text-xl font-bold">Vi behöver lite mer info</h3>
                    <p className="text-muted-foreground text-sm">{reason}</p>
                    <Button className="bg-[#374ef4] hover:bg-[#2a3fd4] text-white font-semibold w-full" onClick={back}>
                        <ArrowLeft className="mr-2 h-4 w-4" /> Gå tillbaka och fyll i
                    </Button>
                </Card>
            </div>
        );
    }

    const missingItemsSection = result.missing_items && result.missing_items.length > 0 ? (() => {
        const grouped = getMissingByStep(result.missing_items, slug);
        return (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
                <p className="font-semibold text-sm text-amber-900">
                    Komplettera för ett smalare spann
                </p>
                {grouped.map(({ step: ms, label, items }) => (
                    <div key={ms} className="rounded-lg bg-white border border-amber-200 p-3">
                        <p className="text-xs font-bold text-amber-800 mb-1.5">{label}</p>
                        <ul className="space-y-1 mb-2.5">
                            {items.map((item, i) => (
                                <li key={i} className="flex items-start gap-2 text-xs text-amber-800">
                                    <div className="h-3.5 w-3.5 rounded-full border-2 border-amber-400 shrink-0 mt-0.5" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                        <Button
                            size="sm"
                            className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold"
                            onClick={() => goToStep(ms)}
                        >
                            Gå till {label} <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                        </Button>
                    </div>
                ))}
            </div>
        );
    })() : null;

    if (!priceRevealed) {
        return (
            <div className="space-y-6 animate-in fade-in duration-500">
                <Card className="border-2 border-primary/20 p-8 text-center space-y-4 shadow-md">
                    <Shield className="h-12 w-12 mx-auto text-[#374ef4]" />
                    <h3 className="text-xl font-bold">Ett sista steg</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed max-w-md mx-auto">
                        Din prisindikation är beräknad. Lämna dina kontaktuppgifter så visar vi summan och skickar din förfrågan till oss.
                    </p>
                    <Button
                        className="w-full min-h-12 bg-[#374ef4] hover:bg-[#2a3fd4] text-white font-bold"
                        onClick={() => onBook?.('video')}
                    >
                        Få pris nu <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <p className="text-xs text-muted-foreground">
                        {data.customer_type !== 'foretag' ? `${PROMO_30_KITCHEN}. ` : ''}Vi bekräftar snabbt — inga telefonförsäljare.
                    </p>
                </Card>
                {missingItemsSection}
                <div className="flex flex-col sm:flex-row gap-3 sm:justify-between">
                    <Button variant="outline" onClick={back} className="w-full sm:w-auto">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Tillbaka
                    </Button>
                </div>
            </div>
        );
    }

    // Accuracy level config per status_badge
    const accuracyConfig = {
        'Fastpris redo':      { label: 'Nära exakt pris',      sub: 'Underlaget är komplett. Spannet är smalt.',            color: 'bg-green-400',  dots: 4 },
        'Preliminärt pris':   { label: 'Ganska säker uppskattning', sub: 'Lite mer underlag gör priset ännu säkrare.',        color: 'bg-[#374ef4]', dots: 3 },
        'Grov uppskattning':  { label: 'Grov uppskattning',    sub: 'Komplettera underlaget för ett smalare spann.',         color: 'bg-[#c65d3b]', dots: 2 },
    };
    const ac = accuracyConfig[result.status_badge] || accuracyConfig['Grov uppskattning'];

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">

            <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
                <p className="font-semibold">Tack — vi har tagit emot din förfrågan.</p>
                {submittedCaseNumber != null && (
                    <p className="text-green-800/90 mt-1">Ärendenummer: <span className="font-mono font-bold">{submittedCaseNumber}</span></p>
                )}
                {onContinueToThanks && (
                    <Button
                        type="button"
                        className="mt-3 w-full sm:w-auto bg-green-700 hover:bg-green-800 text-white"
                        onClick={onContinueToThanks}
                    >
                        Gå till bekräftelsesidan <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                )}
            </div>

            {/* ── PRISKORTET ── */}
            <div className="rounded-2xl bg-primary text-white overflow-hidden shadow-xl">
                <div className="px-6 pt-6 pb-2 text-center">
                    <p className="text-sm font-semibold uppercase tracking-wider text-white/70 mb-1">
                        {isFixed ? 'Rekommenderat fastpris' : 'Uppskattad kostnad'}
                    </p>
                    <div className="text-6xl font-extrabold tracking-tight animate-in fade-in zoom-in-95 duration-300">
                        {displayPrice.toLocaleString('sv-SE')}
                        <span className="text-3xl font-semibold ml-1">kr</span>
                    </div>
                    {data.customer_type === 'privat' && rotDeduction > 0 && (
                        <p className="text-sm text-white/70 mt-1">Inkl. moms, efter ROT‑avdrag (−{Math.round(rotDeduction).toLocaleString('sv-SE')} kr)</p>
                    )}
                </div>

                {/* Spann-bar */}
                <div className="px-6 py-4">
                    <div className="flex justify-between text-xs text-white/60 mb-1">
                        <span>{displayPriceLow.toLocaleString('sv-SE')} kr</span>
                        <span className="text-white/80">±{spanPct}% spann</span>
                        <span>{displayPriceHigh.toLocaleString('sv-SE')} kr</span>
                    </div>
                    <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                        <div className="h-full bg-white/70 rounded-full" style={{ width: '100%' }} />
                    </div>
                </div>

                {/* Accuracy strip */}
                <div className="mx-4 mb-4 rounded-xl bg-white/10 px-4 py-3 flex items-center gap-3">
                    <div className="flex gap-1 shrink-0">
                        {[1,2,3,4].map(i => (
                            <div key={i} className={`h-2 w-5 rounded-full ${i <= ac.dots ? ac.color : 'bg-white/20'}`} />
                        ))}
                    </div>
                    <div className="min-w-0">
                        <p className="text-xs font-semibold text-white leading-tight">{ac.label}</p>
                        <p className="text-[11px] text-white/60 leading-tight mt-0.5">{ac.sub}</p>
                    </div>
                </div>

                {/* Tid + dela */}
                <div className="grid grid-cols-2 divide-x divide-white/10 border-t border-white/10">
                    <div className="px-4 py-3 text-center">
                        <div className="text-xs text-white/60 mb-0.5">Uppskattad tid</div>
                        <div className="font-bold text-lg">{daysText}</div>
                        <div className="text-xs text-white/50">{Math.round(result.hours_median || 16)} tim</div>
                    </div>
                    <div className="px-4 py-3 flex items-center justify-center gap-3">
                        <button
                            type="button"
                            className="flex flex-col items-center gap-0.5 transition-colors text-white/70 hover:text-white"
                            onClick={() => {
                                navigator.clipboard.writeText(
                                    `${catTitle} via Klart Hemma\n${displayPrice.toLocaleString('sv-SE')} kr\nSpann: ${displayPriceLow.toLocaleString('sv-SE')} till ${displayPriceHigh.toLocaleString('sv-SE')} kr`
                                );
                                toast.success('Kopierat!');
                            }}
                        >
                            <Clipboard className="h-5 w-5" />
                            <span className="text-[10px]">Kopiera</span>
                        </button>
                        <button
                            type="button"
                            className="flex flex-col items-center gap-0.5 transition-colors text-white/70 hover:text-white"
                            onClick={() => window.print()}
                        >
                            <Printer className="h-5 w-5" />
                            <span className="text-[10px]">Skriv ut</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Efter kontakt + pris: ingen ny dialog-CTA här ── */}
            <div className="space-y-3">
                <p className="text-center text-sm text-muted-foreground">
                    Vill du boka videosamtal eller stämma av detaljer? Ring oss gärna på{' '}
                    <a href="tel:0707388602" className="font-semibold text-primary">0707&nbsp;388&nbsp;602</a>.
                </p>
                <p className="text-center text-xs font-medium text-green-900 bg-green-100 rounded-xl px-4 py-2.5 leading-snug">
                    {data.customer_type !== 'foretag' ? `${PROMO_30_KITCHEN}. ` : ''}Vi kontaktar dig för att säkerställa att priset stämmer överens med dina önskemål.
                </p>
            </div>

            {/* ── INGÅR I PRISET ── */}
            {result.scope_included && result.scope_included.length > 0 && (
                <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="scope" className="border rounded-xl overflow-hidden">
                        <AccordionTrigger className="px-4 py-3 hover:no-underline">
                            <span className="flex items-center gap-2 font-semibold text-sm">
                                <ListChecks className="h-4 w-4 text-accent" /> Vad ingår i priset?
                            </span>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                            <ul className="space-y-1.5 text-sm">
                                {result.scope_included.slice(0, 10).map((item, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                        <CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" /> {item}
                                    </li>
                                ))}
                            </ul>
                            {result.scope_excluded && result.scope_excluded.length > 0 && (
                                <div className="mt-3 pt-3 border-t">
                                    <p className="text-xs font-semibold text-muted-foreground mb-1">Ingår ej:</p>
                                    <ul className="text-xs text-muted-foreground space-y-0.5">
                                        {result.scope_excluded.slice(0, 5).map((item, i) => (
                                            <li key={i}>• {item}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            )}

            {/* ── SMALNA SPANNET ── */}
            {missingItemsSection}

            {/* ── SEKUNDÄR CTA ── */}
            <div className="rounded-xl border-2 border-slate-100 p-4 flex items-center gap-4">
                <div className="bg-primary/10 rounded-full p-2 shrink-0">
                    <MessageSquare className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">Föredrar du ett snabbt samtal?</p>
                    <p className="text-xs text-muted-foreground">Ring oss direkt på 0707 388 602</p>
                </div>
                <a
                    href="tel:0707388602"
                    className="shrink-0 inline-flex items-center px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-semibold hover:bg-slate-50 transition-colors"
                >
                    Ring nu
                </a>
            </div>

            {/* ── FAQ ── */}
            <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="faq" className="border rounded-xl overflow-hidden">
                    <AccordionTrigger className="px-4 py-3 hover:no-underline font-semibold text-sm">
                        Vanliga frågor
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 space-y-3 text-sm text-slate-600">
                        <p><strong>Vad påverkar priset mest?</strong> Antal stommar, anpassningar och logistik.</p>
                        <p><strong>När kan jag få fastpris?</strong> När vi har komplett underlag: stommar, mått och bilder.</p>
                        <p><strong>Hur fungerar el och VVS?</strong> Du väljer nivå. Behöriga arbeten utförs av certifierade partners.</p>
                        <p><strong>{PROMO_30_KITCHEN}?</strong> Ja, för privatpersoner: ROT ger 30 % på arbetskostnaden (upp till Skatteverkets tak).</p>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>

            {/* ── TRUST ── */}
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-5">
                <div className="grid grid-cols-3 gap-3 text-center">
                    {[
                        { icon: <Shield className="h-5 w-5 text-accent mx-auto mb-1" />, text: 'Fasta priser utan överraskningar' },
                        { icon: <CheckCircle className="h-5 w-5 text-accent mx-auto mb-1" />, text: 'En kontakt hela vägen' },
                        { icon: <RefreshCw className="h-5 w-5 text-accent mx-auto mb-1" />, text: 'Tydlig process & avstämning' },
                    ].map((t, i) => (
                        <div key={i} className="space-y-0.5">
                            {t.icon}
                            <p className="text-xs text-slate-600 leading-snug">{t.text}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── PDF + BOOKING ── */}
            <div className="flex gap-2">
                <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => downloadPDF(category || 'kok', result, contactForm)}
                >
                    <Download className="mr-2 h-4 w-4" /> Ladda ner PDF
                </Button>
                {bookingUrl && (
                    <Button
                        variant="outline"
                        className="flex-1"
                        asChild
                    >
                        <a href={bookingUrl} target="_blank" rel="noopener noreferrer">
                            <Calendar className="mr-2 h-4 w-4" /> Boka tid
                        </a>
                    </Button>
                )}
            </div>

        </div>
    );
};

// ── PDF export helper ─────────────────────────────────────────────────────────

function resolvePublicUploadUrl(url) {
  if (!url || typeof url !== 'string') return '';
  if (/^https?:\/\//i.test(url)) return url;
  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  return `${base}${url}`;
}

async function buildQuotePdfDoc(category, result, contact) {
  const jsPDF = (await import('jspdf')).default;
  const autoTable = (await import('jspdf-autotable')).default;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  const fmt = (n) => Math.round(n || 0).toLocaleString('sv-SE');
  const catLabel = getCategoryBySlug(category)?.label || category;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const m = 18;
  const contentW = pageW - m * 2;
  const brand = [55, 78, 244];
  const ink = [27, 36, 33];
  const slate = [88, 97, 90];
  const line = [216, 214, 204];

  const showRot = category === 'kok' && result.rot_deduction > 0;

  // Top accent stripe (design language)
  doc.setFillColor(...brand);
  doc.rect(0, 0, pageW, 3.2, 'F');

  let y = 11;

  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...ink);
  doc.text('Klart Hemma', m, y + 6);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...slate);
  doc.text('AB', m, y + 10);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...slate);
  doc.text(new Date().toLocaleDateString('sv-SE'), pageW - m, y + 3, { align: 'right' });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...brand);
  doc.text('Prisindikation', pageW - m, y + 7.5, { align: 'right' });

  y = 24;
  doc.setDrawColor(...line);
  doc.setLineWidth(0.25);
  doc.line(m, y, pageW - m, y);
  y += 7;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...ink);
  doc.text('Uppdrag', m, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...slate);
  doc.text(`Kategori: ${catLabel}`, m, y);
  y += 5;

  if (contact?.name || contact?.address_street) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...ink);
    doc.text('Kund', m, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...slate);
    if (contact?.name) {
      doc.text(contact.name, m, y);
      y += 4.5;
    }
    if (contact?.address_street) {
      const addr = `${contact.address_street} ${contact.address_number || ''}`.trim();
      const line2 = `${contact.address_zip || ''} ${contact.address_city || ''}`.trim();
      doc.text(addr, m, y);
      y += 4.5;
      if (line2.trim()) {
        doc.text(line2, m, y);
        y += 4.5;
      }
    }
    y += 3;
  } else {
    y += 2;
  }

  // Price panel (subtle box, prints cleanly)
  const boxTop = y;
  let boxH = 22;
  if (showRot) boxH += 12;
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(...brand);
  doc.setLineWidth(0.35);
  doc.rect(m, boxTop, contentW, boxH, 'FD');

  y = boxTop + 7;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...slate);
  doc.text('Rekommenderat pris (inkl. moms)', m + 4, y);
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...ink);
  doc.text(`${fmt(result.price_rec)} kr`, m + 4, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...slate);
  doc.text(`Spann: ${fmt(result.price_low)} – ${fmt(result.price_high)} kr`, m + 4, y);
  if (showRot) {
    y += 5;
    doc.setTextColor(21, 128, 61);
    doc.text(`ROT-avdrag (uppskattning): −${fmt(result.rot_deduction)} kr`, m + 4, y);
    y += 5;
    doc.setTextColor(...ink);
    doc.setFont('helvetica', 'bold');
    doc.text(`Efter ROT: ${fmt(result.price_rec - result.rot_deduction)} kr`, m + 4, y);
    doc.setFont('helvetica', 'normal');
  }

  y = boxTop + boxH + 8;

  if (result.scope_included && result.scope_included.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...ink);
    doc.text('Vad ingår i priset', m, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...slate);
    result.scope_included.slice(0, 14).forEach((item) => {
      const lines = doc.splitTextToSize(`• ${item}`, contentW - 4);
      lines.forEach((ln) => {
        doc.text(ln, m + 2, y);
        y += 4.2;
      });
    });
    y += 3;
  }

  let endY = y;
  if (result.breakdown && result.breakdown.length > 0) {
    autoTable(doc, {
      startY: y,
      margin: { left: m, right: m },
      head: [['Del', 'Beskrivning', 'Belopp / tid']],
      body: result.breakdown.map((r) => [
        r.category,
        r.description,
        r.cost > 0 ? `${fmt(r.cost)} kr` : `${Math.round(r.total_med * 10) / 10} tim`,
      ]),
      styles: {
        fontSize: 8,
        cellPadding: { top: 2, right: 2, bottom: 2, left: 2 },
        textColor: ink,
        lineColor: line,
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: brand,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
      },
      alternateRowStyles: { fillColor: [252, 252, 253] },
    });
    endY = doc.lastAutoTable.finalY + 10;
  }

  const footerBand = 9;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  const disclaimer = doc.splitTextToSize(
    'Priset är en uppskattning och kan justeras efter besiktning och slutligt underlag. Bindande avtal sker skriftligt.',
    contentW
  );
  const discH = disclaimer.length * 3.4 + 2;
  if (endY + discH > pageH - footerBand) {
    doc.addPage();
    endY = 18;
  }
  disclaimer.forEach((lineTxt, idx) => {
    doc.text(lineTxt, m, endY + idx * 3.4);
  });

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(148, 163, 184);
    doc.text(
      `Klart Hemma AB  ·  0707 388 602  ·  kontakt@klarthemma.com  ·  Sida ${i}/${pageCount}`,
      pageW / 2,
      pageH - 6,
      { align: 'center' }
    );
  }

  return { doc, catLabel };
}

async function buildQuotePdfBlob(category, result, contact) {
  const { doc, catLabel } = await buildQuotePdfDoc(category, result, contact);
  const blob = doc.output('blob');
  const safe = (catLabel || category || 'offert').replace(/\s+/g, '-');
  return { blob, filename: `Klart-Hemma-offert-${safe}.pdf` };
}

async function downloadPDF(category, result, contact) {
  try {
    const { doc, catLabel } = await buildQuotePdfDoc(category, result, contact);
    doc.save(`Klart-Hemma-offert-${catLabel.replace(/\s+/g, '-')}.pdf`);
  } catch (err) {
    console.error('PDF error', err);
    toast.error('Kunde inte generera PDF. Försök igen.');
  }
}

// ── Shared field helpers (module-level to prevent focus loss on re-render) ──

const NumInput = ({ lbl, field, data, update, placeholder, unit, required, error }) => (
  <div>
    <Label className="text-sm text-muted-foreground mb-1 block">
      {lbl}{required && <span className="text-red-500 ml-0.5"> *</span>}
    </Label>
    <div className="flex items-center gap-2">
      <Input
        type="text"
        inputMode="decimal"
        placeholder={placeholder || '0'}
        value={data[field] != null ? String(data[field]) : ''}
        onChange={(e) => update(field, e.target.value)}
        className={`w-32${error ? ' border-red-400' : ''}`}
      />
      {unit && <span className="text-sm text-muted-foreground">{unit}</span>}
    </div>
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

const FieldSelect = ({ lbl, field, data, update, options, hint, required, error, allowEmpty }) => {
  const EMPTY_SELECT_VALUE = '__empty__';
  const currentVal = (data[field] != null && data[field] !== '')
    ? String(data[field])
    : (allowEmpty ? EMPTY_SELECT_VALUE : (options[0]?.value ?? ''));
  return (
    <div>
      <Label className="mb-1 block font-medium text-sm">
        {lbl}{required && <span className="text-red-500 ml-0.5"> *</span>}
      </Label>
      <Select
        value={currentVal}
        onValueChange={(v) => update(field, allowEmpty && v === EMPTY_SELECT_VALUE ? '' : v)}
      >
        <SelectTrigger className={error ? 'border-red-400' : ''}>
          <SelectValue placeholder="Välj..." />
        </SelectTrigger>
        <SelectContent>
          {allowEmpty && <SelectItem value={EMPTY_SELECT_VALUE}>Välj...</SelectItem>}
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

// ── CategoryScopeStep (Step 2 for non-kitchen) ────────────────────────────────

const CategoryScopeStep = ({ category, data, update, next, back, errors, hideNav }) => {
  const cat = getCategoryBySlug(category);
  const label = cat?.label || category;

  const renderFields = () => {
    switch (category) {
      case 'malning':
        return (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <NumInput lbl="Kvm vägg" field="painting_walls" data={data} update={update} placeholder="t.ex. 40" unit="m²"
                required error={errors?.painting_area} />
              <NumInput lbl="Kvm tak" field="painting_ceiling" data={data} update={update} placeholder="t.ex. 15" unit="m²" />
            </div>
            {errors?.painting_area && <p className="text-red-500 text-xs -mt-3">{errors.painting_area}</p>}
            <p className="text-xs text-muted-foreground -mt-3 mb-1">Antingen kvm vägg eller kvm tak kan vara 0, men båda kan inte vara 0 om du inte fyller i tapetsering/rivning.</p>
            <NumInput lbl="Tapetsering (m²)" field="wallpaper_area" data={data} update={update} placeholder="0" unit="m²" />
            <NumInput lbl="Tapetrivning (m²)" field="strip_wallpaper_area" data={data} update={update} placeholder="0" unit="m²" />
            {malningNeedsWallLength(data) && (
              <NumInput lbl="Längd på väggar (m)" field="paint_wall_length_m" data={data} update={update}
                placeholder="t.ex. 16" unit="m" required error={errors?.paint_wall_length_m} />
            )}
            <NumInput lbl="Lister/kanter (m)" field="trim_length" data={data} update={update} placeholder="0" unit="m" />
            <FieldSelect lbl="Spackling" field="spackle" data={data} update={update} options={[
              { value: 'none', label: 'Ingen spackling' },
              { value: 'light', label: 'Lätt spackling (+10%)' },
              { value: 'medium', label: 'Normal spackling (+20%)' },
              { value: 'heavy', label: 'Kraftig spackling (+35%)' },
            ]} />
            <FieldSelect lbl="Möbler i rummet" field="furniture" data={data} update={update} options={[
              { value: 'none', label: 'Inga möbler' },
              { value: 'some', label: 'Lite möbler (+10%)' },
              { value: 'lots', label: 'Många möbler (+25%)' },
            ]} />
          </div>
        );

      case 'vatrum':
        return (
          <div className="space-y-5">
            <FieldSelect lbl="Typ av renovering" field="wet_scope" data={data} update={update} allowEmpty required
              error={errors?.wet_scope} options={[
                { value: 'ytskikt', label: 'Ytskikt: kakel/klinker utan total rivning' },
                { value: 'del', label: 'Delrenovering: ytskikt + tätskikt' },
                { value: 'hel', label: 'Helrenovering: rivning till stomme' },
              ]} />
            <div>
              <Label className="mb-1 block font-medium text-sm">Mått på badrummet <span className="text-red-500">*</span></Label>
              <div className="grid grid-cols-2 gap-3">
                <NumInput lbl="Längd (cm)" field="wet_length_cm" data={data} update={update} placeholder="t.ex. 250" unit="cm" error={errors?.wet_dims} />
                <NumInput lbl="Bredd (cm)" field="wet_width_cm" data={data} update={update} placeholder="t.ex. 180" unit="cm" />
              </div>
            </div>
            <NumInput lbl="Yta (m²)" field="wet_area" data={data} update={update} placeholder="Räknas ut från måtten" unit="m²"
              required error={errors?.wet_area} />
            <FieldSelect lbl="Storlek på badrummet" field="wet_size" data={data} update={update} allowEmpty required
              error={errors?.wet_size} options={[
                { value: 'small', label: 'Litet (upp till 5 m²)' },
                { value: 'standard', label: 'Standard (5-10 m²)' },
                { value: 'large', label: 'Stort (10+ m²)' },
              ]} />
            <div className="flex items-center gap-3">
              <Checkbox checked={data.wet_unknown || false} onCheckedChange={(v) => update('wet_unknown', v)} />
              <Label className="text-sm">Osäker på skick/konstruktion (+15%)</Label>
            </div>
          </div>
        );

      case 'kakel':
        return (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <NumInput lbl="Kakelyta vägg (m²)" field="tile_wall_area" data={data} update={update} placeholder="0" unit="m²" />
              <NumInput lbl="Kakelyta golv (m²)" field="tile_floor_area" data={data} update={update} placeholder="0" unit="m²" />
            </div>
            {errors?.tile_area && <p className="text-red-500 text-xs -mt-3">{errors.tile_area}</p>}
            <NumInput lbl="Rivning av gammalt kakel (m²)" field="tile_demo_area" data={data} update={update} placeholder="0" unit="m²" />
            <NumInput lbl="Antal hörnskärningar" field="tile_corners" data={data} update={update} placeholder="0" unit="st" />
            <FieldSelect lbl="Kakeltyp" field="tile_type" data={data} update={update} allowEmpty required
              error={errors?.tile_type} options={[
                { value: 'standard', label: 'Standard' },
                { value: 'mosaic', label: 'Mosaik (+35%)' },
                { value: 'large', label: 'Stort format 60×60+ (+15%)' },
                { value: 'concrete', label: 'Betong/sten (+15%)' },
              ]} />
            <FieldSelect lbl="Underlagsförberedelse" field="tile_prep" data={data} update={update} options={[
              { value: 'good', label: 'Bra underlag' },
              { value: 'light', label: 'Lätt avvägning (+15%)' },
              { value: 'heavy', label: 'Kraftig avvägning/skador (+35%)' },
            ]} />
          </div>
        );

      case 'golv':
        return (
          <div className="space-y-5">
            <NumInput lbl="Golvyta (m²)" field="floor_area" data={data} update={update} placeholder="t.ex. 30" unit="m²"
              required error={errors?.floor_area} />
            <FieldSelect lbl="Golvtyp" field="floor_type" data={data} update={update} options={[
              { value: 'laminate', label: 'Laminat' },
              { value: 'wood', label: 'Trägolv/parkett' },
              { value: 'vinyl', label: 'Vinyl/LVT' },
              { value: 'carpet', label: 'Heltäckningsmatta' },
              { value: 'sand', label: 'Slipning av befintligt trägolv' },
            ]} hint="Välj 'Slipning' om du vill renovera befintligt parkettgolv." />
            {data.floor_type === 'sand' && (
              <FieldSelect lbl="Ytbehandling efter slipning" field="floor_finish" data={data} update={update}
                allowEmpty required error={errors?.floor_finish} options={[
                  { value: 'lacquer', label: 'Lackering' },
                  { value: 'oil', label: 'Oljebehandling' },
                ]} />
            )}
            {data.floor_type !== 'sand' && (
              <FieldSelect lbl="Ytbehandling" field="floor_finish" data={data} update={update} options={[
                { value: 'none', label: 'Ingen (fabriksbehandlat)' },
                { value: 'lacquer', label: 'Lackering' },
                { value: 'oil', label: 'Oljebehandling' },
              ]} />
            )}
            <NumInput lbl="Rivning av gammalt golv (m²)" field="floor_remove_area" data={data} update={update} placeholder="0" unit="m²" />
            <NumInput lbl="Socklar/lister (m)" field="baseboard_length" data={data} update={update} placeholder="0" unit="m" />
            <FieldSelect lbl="Möbler i rummet" field="furniture" data={data} update={update} options={[
              { value: 'none', label: 'Inga möbler' },
              { value: 'some', label: 'Lite möbler (+10%)' },
              { value: 'lots', label: 'Många möbler (+25%)' },
            ]} />
          </div>
        );

      case 'vvs':
        return (
          <div className="space-y-5">
            <FieldSelect lbl="Typ av VVS-arbete" field="vvs_job_type" data={data} update={update}
              allowEmpty required error={errors?.vvs_job_type} options={[
                { value: 'inkoppling', label: 'Inkoppling (ny diskho, dusch, tvättmaskin)' },
                { value: 'byte', label: 'Byte av armatur' },
                { value: 'stopp', label: 'Stopp / Läckage (rensning, tätning)' },
                { value: 'flytt', label: 'Flytt / Omdragning (rör, element)' },
              ]} />
            {data.vvs_job_type === 'byte' && (
              <div className="space-y-2 pl-1 border-l-2 border-accent/40">
                <Label className="text-sm font-medium block pl-2">Vad ska bytas? <span className="text-red-500">*</span></Label>
                <div className="flex items-center gap-3 pl-2">
                  <Checkbox id="vvs-mixer" checked={data.vvs_replace_mixer || false} onCheckedChange={(v) => update('vvs_replace_mixer', v)} />
                  <Label htmlFor="vvs-mixer" className="text-sm">Blandare</Label>
                </div>
                <div className="flex items-center gap-3 pl-2">
                  <Checkbox id="vvs-toilet" checked={data.vvs_replace_toilet || false} onCheckedChange={(v) => update('vvs_replace_toilet', v)} />
                  <Label htmlFor="vvs-toilet" className="text-sm">Toalett</Label>
                </div>
                {errors?.vvs_byte_parts && <p className="text-red-500 text-xs pl-2">{errors.vvs_byte_parts}</p>}
              </div>
            )}
            <FieldSelect lbl="Storlek på jobbet" field="vvs_job_size" data={data} update={update} options={[
              { value: 'small', label: 'Litet (enkel anslutning/byte)' },
              { value: 'standard', label: 'Standard' },
              { value: 'large', label: 'Stort (fler anslutningar / komplex)' },
            ]} />
            <FieldSelect lbl="Åtkomlighet" field="vvs_access" data={data} update={update} options={[
              { value: 'good', label: 'Bra åtkomlighet' },
              { value: 'hard', label: 'Begränsad åtkomlighet (+10%)' },
              { value: 'none', label: 'Inget inspektionslock (+25%)' },
            ]} />
            <div className="flex items-center gap-3">
              <Checkbox checked={data.vvs_concrete_wall || false} onCheckedChange={(v) => update('vvs_concrete_wall', v)} />
              <Label className="text-sm">Betonggolv/-vägg (kräver borrning, +15%)</Label>
            </div>
            <div>
              <Label className="mb-1 block font-medium text-sm">
                Mer information <span className="text-red-500">*</span>
              </Label>
              <textarea
                className={`flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px]${errors?.extra_notes ? ' border-red-400' : ''}`}
                placeholder="Beskriv arbetet i detalj, t.ex. var i bostaden, vad som ska åtgärdas, om det rör trycksatt vatten"
                value={data.extra_notes || ''}
                onChange={(e) => update('extra_notes', e.target.value)}
              />
              {errors?.extra_notes && <p className="text-red-500 text-xs mt-1">{errors.extra_notes}</p>}
            </div>
          </div>
        );

      case 'el':
        return (
          <div className="space-y-5">
            <FieldSelect lbl="Typ av elarbete" field="el_job_type" data={data} update={update}
              allowEmpty required error={errors?.el_job_type} options={[
                { value: 'uttag_armatur', label: 'Uttag / Armatur' },
                { value: 'belysning', label: 'Belysning / Spottar' },
                { value: 'ny_grupp', label: 'Ny grupp / Matning (laddbox, bastu)' },
                { value: 'felsok', label: 'Felsökning' },
              ]} />
            <FieldSelect lbl="Storlek på jobbet" field="el_job_size" data={data} update={update}
              allowEmpty required error={errors?.el_job_size} options={[
                { value: 'small', label: 'Litet (enstaka uttag/armatur)' },
                { value: 'standard', label: 'Standard' },
                { value: 'large', label: 'Stort (flera rum / installation)' },
              ]} />
            <div className="space-y-2">
              {[
                { field: 'el_wall_open', label: 'Öppning av vägg/tak krävs (+20%)' },
                { field: 'el_long_run', label: 'Lång kabeldragning (+15%)' },
                { field: 'el_concrete_wall', label: 'Betongvägg/-golv (+15%)' },
              ].map(({ field, label: lbl }) => (
                <div key={field} className="flex items-center gap-3">
                  <Checkbox checked={data[field] || false} onCheckedChange={(v) => update(field, v)} />
                  <Label className="text-sm">{lbl}</Label>
                </div>
              ))}
            </div>
          </div>
        );

      case 'snickeri':
        return (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <NumInput lbl="Innervägg (m)" field="partition_length" data={data} update={update} placeholder="0" unit="m" />
              <NumInput lbl="Undertak (m²)" field="ceiling_area" data={data} update={update} placeholder="0" unit="m²" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <NumInput lbl="Lister/socklar (m)" field="trim_length" data={data} update={update} placeholder="0" unit="m" />
              <NumInput lbl="Innerdörrar (st)" field="interior_doors" data={data} update={update} placeholder="0" unit="st" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <NumInput lbl="Ytterdörrar (st)" field="exterior_doors" data={data} update={update} placeholder="0" unit="st" />
              <NumInput lbl="Dörrfoder (st dörrar)" field="casing_doors" data={data} update={update} placeholder="0" unit="st" />
            </div>
            {errors?.snickeri_work && <p className="text-red-500 text-xs">{errors.snickeri_work}</p>}
            <FieldSelect lbl="Ombyggnad" field="remodel_size" data={data} update={update} options={[
              { value: 'none', label: 'Ingen ombyggnad' },
              { value: 'small', label: 'Liten ombyggnad' },
              { value: 'medium', label: 'Medel ombyggnad' },
              { value: 'large', label: 'Stor ombyggnad' },
            ]} hint="Kombinationsarbeten som att flytta dörrar eller öppna upp." error={errors?.snickeri_work} />
          </div>
        );

      default:
        return <p className="text-muted-foreground text-sm">Fyll i information om arbetet nedan.</p>;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-primary">Beskriv arbetet</h2>
        <p className="text-muted-foreground text-sm mt-1">{label}. Fyll i det du vet. Exaktare uppgifter ger ett smalare spann.</p>
      </div>
      {renderFields()}
      {!hideNav && (
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-between mt-6">
          <Button variant="outline" onClick={back} className="w-full sm:w-auto"><ArrowLeft className="mr-2 h-4 w-4" /> Tillbaka</Button>
          <Button onClick={next} className="w-full sm:w-auto bg-[#374ef4] hover:bg-[#2a3fd4] text-white">Nästa <ArrowRight className="ml-2 h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
};

// ── CategoryAddonsStep (Step 3 for non-kitchen) ───────────────────────────────

const CategoryAddonsStep = ({ category, data, update, next, back, hideNav }) => {
  const renderAddons = () => {
    const logisticsField = (
      <FieldSelect lbl="Logistik" field="logistics" data={data} update={update} options={[
        { value: 'bra', label: 'Bra (nära parkering / hiss)' },
        { value: 'normal', label: 'Normal (trappor / bärväg)' },
        { value: 'svar', label: 'Svår (många trappor / trångt)' },
      ]} hint="Hur lätt är det att bära in material?" />
    );

    const extraNotesField = (label = 'Mer information') => (
      <div>
        <Label className="mb-1 block font-medium text-sm">{label}</Label>
        <textarea
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px]"
          placeholder="t.ex. 'befintliga rör i betong', 'ingen inspektionsöppning'"
          value={data.extra_notes || ''}
          onChange={(e) => update('extra_notes', e.target.value)}
        />
      </div>
    );

    switch (category) {
      case 'malning':
        return (
          <div className="space-y-4">
            {logisticsField}
            <div className="flex items-center gap-3">
              <Checkbox checked={data.high_ceiling || false} onCheckedChange={(v) => update('high_ceiling', v)} />
              <Label className="text-sm">Takhöjd &gt;3 m (kräver stege/ställning, +10%)</Label>
            </div>
            <div className="flex items-center gap-3">
              <Checkbox checked={data.masking === 'detailed'} onCheckedChange={(v) => update('masking', v ? 'detailed' : 'normal')} />
              <Label className="text-sm">Detaljerad avmaskning (lister, fönsterkitt, +10%)</Label>
            </div>
          </div>
        );
      case 'kakel':
      case 'golv':
        return <div className="space-y-4">{logisticsField}</div>;
      case 'snickeri':
        return (
          <div className="space-y-4">
            {logisticsField}
            {extraNotesField('Mer information')}
          </div>
        );
      case 'vatrum':
        return (
          <div className="space-y-4">
            {extraNotesField('Mer information')}
          </div>
        );
      case 'el':
        return (
          <div className="space-y-4">
            {extraNotesField('Mer information')}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-primary">{category === 'vatrum' || category === 'el' ? 'Något mer vi ska veta?' : 'Tillägg & svårighet'}</h2>
        {category !== 'vatrum' && category !== 'el' && <p className="text-muted-foreground text-sm mt-1">Välj de tillägg som passar projektet.</p>}
      </div>
      {renderAddons()}
      {!hideNav && (
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-between mt-6">
          <Button variant="outline" onClick={back} className="w-full sm:w-auto"><ArrowLeft className="mr-2 h-4 w-4" /> Tillbaka</Button>
          <Button onClick={next} className="w-full sm:w-auto bg-[#374ef4] hover:bg-[#2a3fd4] text-white">Nästa <ArrowRight className="ml-2 h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
};

// Main Wizard Page
export default function WizardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveEmail, setSaveEmail] = useState("");
  
  const isEmbedded = searchParams.get('embed') === '1';

  // Category from URL - default to 'kok' for backward compat
  const categoryFromUrl = searchParams.get('category') || 'kok';
  const isKitchen = categoryFromUrl === 'kok';
  const catConfig = getCategoryBySlug(categoryFromUrl);

  const [formData, setFormData] = useState({
    category: categoryFromUrl,
    subcategory: searchParams.get('subcategory') || '',
    customer_type: 'privat',
    price_model: 'fastpris',
    // Standardkök (8U + 6V + 2H) som normal utgångspunkt
    u_cabinets: 8,
    v_cabinets: 6,
    h_cabinets: 2,
    island: 'ingen',
    worktop: 'laminat',
    panels: 'medel',
    floor_laid: true,
    demolition: false,
    disposal: false,
    disposal_runs: 1,
    // Normal nivå: inga el/VVS‑paket förifyllda
    electrical: 'ingen',
    plumbing: 'ingen',
    logistics: 'normal',
    material_status: 'owned',
    // Vanliga vitvaror för ett normalstort kök
    integrated_dishwasher: 1,
    oven_micro: 1,
    integrated_fridge_freezer: 1,
    hob: 1,
    fan: 1,
    backsplash: false,
    backsplash_length: 0,
    backsplash_wall_type: 'vet_ej',
    photos: [],
    dimensions: { wall_a: 0, wall_b: 0, ceiling_height: 2400 },
    painting_walls: 0,
    painting_ceiling: 0
  });
  const [result, setResult] = useState(null);

  useEffect(() => {
    const loadDraft = async () => {
      // Check for server-side draft via resume param
      const resumeId = searchParams.get('resume');
      if (resumeId) {
        try {
          const res = await apiClient.get(`/drafts/${resumeId}`);
          if (res.data?.config) {
            setFormData(prev => ({ ...prev, ...res.data.config }));
            setDraftLoaded(true);
            if (res.data.step) setStep(res.data.step);
            return;
          }
        } catch (e) {
          console.log('No server draft found, checking localStorage');
        }
      }
      
      // Fallback to localStorage draft from quick estimate
      const draft = localStorage.getItem('quick_estimate_draft');
      if (draft) {
        const parsed = JSON.parse(draft);
        let updates = { price_model: parsed.price_model || 'fastpris' };
        
        // If coming from quick estimate with cabinet counts
        if (parsed.u_cabinets) updates.u_cabinets = parsed.u_cabinets;
        if (parsed.v_cabinets) updates.v_cabinets = parsed.v_cabinets;
        if (parsed.h_cabinets) updates.h_cabinets = parsed.h_cabinets;
        
        // Or from kitchen_size mapping
        if (!parsed.u_cabinets && parsed.kitchen_size) {
          if (parsed.kitchen_size === 'litet') { updates.u_cabinets = 6; }
          else if (parsed.kitchen_size === 'medel') { updates.u_cabinets = 8; }
          else if (parsed.kitchen_size === 'stort') { updates.u_cabinets = 12; }
        }
        if (!parsed.h_cabinets && parsed.high_cabinets) {
          if (parsed.high_cabinets === '0-1') updates.h_cabinets = 1;
          else if (parsed.high_cabinets === '2-3') updates.h_cabinets = 3;
          else if (parsed.high_cabinets === '4+') updates.h_cabinets = 5;
        }
        
        setFormData(prev => ({ ...prev, ...updates }));
        setDraftLoaded(true);
        localStorage.removeItem('quick_estimate_draft');
      }

      // Read qe_type/qe_size from URL for non-kitchen categories (set by CategoryPage quick estimate)
      const qeType = searchParams.get('qe_type');
      const qeSize = searchParams.get('qe_size');
      if (qeType || qeSize) {
        const category = searchParams.get('category');
        const urlUpdates = {};
        if (category === 'malning' && qeType) {
          // Map quick estimate type to a hint about what walls are involved
          urlUpdates.malning_qe_type = qeType;
        }
        if (Object.keys(urlUpdates).length > 0) {
          setFormData(prev => ({ ...prev, ...urlUpdates }));
        }
      }
    };
    
    loadDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const update = (field, value) => {
    setFormData(prev => {
      const nextData = { ...prev, [field]: value };
      // Bathroom: derive area and size from length × width so the visitor doesn't enter them twice.
      if (field === 'wet_length_cm' || field === 'wet_width_cm') {
        const length = parseFloat(String(nextData.wet_length_cm ?? '').replace(',', '.')) || 0;
        const width = parseFloat(String(nextData.wet_width_cm ?? '').replace(',', '.')) || 0;
        if (length > 0 && width > 0) {
          const area = Math.round((length * width) / 1000) / 10;
          nextData.wet_area = String(area);
          nextData.wet_size = area <= 5 ? 'small' : area <= 10 ? 'standard' : 'large';
          nextData.dimensions = { ...nextData.dimensions, wall_a: String(length), wall_b: String(width) };
        }
      }
      return nextData;
    });
    setErrors(prev => {
      const touched = field === 'wet_length_cm' || field === 'wet_width_cm' ? [field, 'wet_dims', 'wet_area', 'wet_size'] : [field];
      if (!touched.some((key) => prev[key])) return prev;
      const nextErrors = { ...prev };
      touched.forEach((key) => { nextErrors[key] = null; });
      return nextErrors;
    });
  };

  const validateStep = () => {
    const newErrors = {};
    let isValid = true;

    if (step === 2 && isKitchen) {
      const total = formData.u_cabinets + formData.v_cabinets + formData.h_cabinets;
      if (total === 0) {
        newErrors.cabinets = "Fyll i minst 1 skåp/stomme för att fortsätta.";
        isValid = false;
      }
    }

    if (step === 2 && !isKitchen) {
      const n = (f) => parseFloat(String(formData[f] ?? '0').replace(',', '.')) || 0;

      switch (categoryFromUrl) {
        case 'malning': {
          const pw = n('painting_walls'), pc = n('painting_ceiling');
          const wp = n('wallpaper_area'), st = n('strip_wallpaper_area');
          if (pw + pc + wp + st === 0) {
            newErrors.painting_area = "Ange minst en yta: antingen vägg eller tak (eller tapet), så att inte vägg och tak båda är 0 utan annat arbete.";
            isValid = false;
          }
          if (malningNeedsWallLength(formData) && n('paint_wall_length_m') === 0) {
            newErrors.paint_wall_length_m = "Ange längden på väggar.";
            isValid = false;
          }
          break;
        }
        case 'vatrum': {
          if (!formData.wet_scope) {
            newErrors.wet_scope = "Välj typ av renovering.";
            isValid = false;
          }
          if (!formData.wet_size) {
            newErrors.wet_size = "Välj storlek.";
            isValid = false;
          }
          if (n('wet_area') === 0) {
            newErrors.wet_area = "Ange yta (m²).";
            isValid = false;
          }
          if (n('wet_length_cm') === 0 || n('wet_width_cm') === 0) {
            newErrors.wet_dims = "Ange mått (längd och bredd).";
            isValid = false;
          }
          break;
        }
        case 'kakel': {
          if (n('tile_wall_area') + n('tile_floor_area') === 0) {
            newErrors.tile_area = "Ange kakelyta för vägg eller golv (båda kan inte vara 0).";
            isValid = false;
          }
          if (!formData.tile_type) {
            newErrors.tile_type = "Välj kakeltyp.";
            isValid = false;
          }
          break;
        }
        case 'golv': {
          if (n('floor_area') === 0) {
            newErrors.floor_area = "Ange golvyta (m²).";
            isValid = false;
          }
          if (formData.floor_type === 'sand') {
            const finish = formData.floor_finish;
            if (!finish || finish === 'none') {
              newErrors.floor_finish = "Välj lackering eller oljebehandling för slipat golv.";
              isValid = false;
            }
          }
          break;
        }
        case 'vvs': {
          if (!formData.vvs_job_type) {
            newErrors.vvs_job_type = "Välj typ av VVS-arbete.";
            isValid = false;
          }
          if (formData.vvs_job_type === 'byte' && !formData.vvs_replace_mixer && !formData.vvs_replace_toilet) {
            newErrors.vvs_byte_parts = "Välj minst ett alternativ (blandare eller toalett).";
            isValid = false;
          }
          if (!formData.extra_notes?.trim()) {
            newErrors.extra_notes = "Beskriv arbetet med mer information.";
            isValid = false;
          }
          break;
        }
        case 'el': {
          if (!formData.el_job_type) {
            newErrors.el_job_type = "Välj typ av elarbete.";
            isValid = false;
          }
          if (!formData.el_job_size) {
            newErrors.el_job_size = "Välj storlek på jobbet.";
            isValid = false;
          }
          break;
        }
        case 'snickeri': {
          const n2 = (f) => parseFloat(String(formData[f] ?? '0').replace(',', '.')) || 0;
          const hasWork = n2('partition_length') + n2('ceiling_area') + n2('trim_length') +
            n2('interior_doors') + n2('exterior_doors') + n2('casing_doors') > 0;
          const hasRemodel = formData.remodel_size && formData.remodel_size !== 'none';
          if (!hasWork && !hasRemodel) {
            newErrors.snickeri_work = "Fyll i minst ett arbetsmoment eller välj en ombyggnad.";
            isValid = false;
          }
          break;
        }
        default: break;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const cleanData = (data) => {
      return {
          ...data,
          u_cabinets: toNumber(data.u_cabinets),
          v_cabinets: toNumber(data.v_cabinets),
          h_cabinets: toNumber(data.h_cabinets),
          integrated_dishwasher: toNumber(data.integrated_dishwasher),
          oven_micro: toNumber(data.oven_micro),
          integrated_fridge_freezer: toNumber(data.integrated_fridge_freezer),
          hob: toNumber(data.hob),
          fan: toNumber(data.fan),
          painting_walls: toNumber(data.painting_walls),
          painting_ceiling: toNumber(data.painting_ceiling),
          dimensions: {
              ...data.dimensions,
              wall_a: toNumber(data.dimensions?.wall_a),
              wall_b: toNumber(data.dimensions?.wall_b),
              ceiling_height: toNumber(data.dimensions?.ceiling_height)
          }
      };
  };

  const doCalculate = async () => {
    const cleanedConfig = cleanData(formData);
    const payload = {
      config: cleanedConfig,
      photos: formData.photos,
      dimensions: cleanedConfig.dimensions,
    };
    try {
      const res = await apiClient.post('/calculate', payload);
      setResult(res.data);
      setStep(4);
    } catch (e) {
      console.error("Calculation Error", e);
      const d = e.response?.data;
      const detail = typeof d?.detail === "string" ? d.detail : null;
      const errField = typeof d?.error === "string" ? d.error : null;
      const errorMsg =
        detail ||
        errField ||
        (e.code === "ERR_NETWORK" || e.message === "Network Error"
          ? "Kunde inte nå servern. Kontrollera att API körs (t.ex. node-api på port 8000 vid lokal utveckling)."
          : e.message) ||
        "Ett fel uppstod vid beräkning.";
      toast.error(`Kunde inte beräkna pris: ${errorMsg}`);
    }
  };

  const next = async () => {
    if (!validateStep()) return;

    // VVS skips the photos/dimensions step — photos are collected in step 2
    const isVvsSkip = step === 2 && categoryFromUrl === 'vvs';

    if (step === 3 || isVvsSkip) {
      await doCalculate();
    } else {
      setStep(s => s + 1);
    }
  };
  
  const back = () => setStep(s => s - 1);
  const goHome = () => navigate('/');

  const handleSaveDraft = async () => {
      if(!saveEmail) {
          toast.error("Ange e-post");
          return;
      }
      try {
          const cleanedConfig = cleanData(formData);
          const res = await apiClient.post('/save-draft', { email: saveEmail, config: cleanedConfig });
          const draftId = res.data?.draft_id;

          if (draftId) {
            const url = `${window.location.origin}/wizard?resume=${draftId}`;
            try {
              await navigator.clipboard.writeText(url);
              toast.success("Utkast sparat! Länk kopierad.");
            } catch {
              toast.success("Utkast sparat! Kopiera länken i adressfältet.");
            }
          } else {
            toast.success("Utkast sparat!");
          }
          setShowSaveDialog(false);
      } catch(e) {
          toast.error("Kunde inte spara utkast.");
      }
  };

  const [showContactForm, setShowContactForm] = useState(false);
  const [submittingContact, setSubmittingContact] = useState(false);
  const [contactPriceRevealed, setContactPriceRevealed] = useState(false);
  const [submittedCaseNumber, setSubmittedCaseNumber] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    address_street: '',
    address_number: '',
    address_zip: '',
    address_city: '',
    newsletter_consent: false
  });

  useEffect(() => {
    if (step < 4) {
      setContactPriceRevealed(false);
      setSubmittedCaseNumber(null);
    }
  }, [step]);

  // Start each new step at the top instead of where the previous step's buttons were.
  const isFirstStepRender = React.useRef(true);
  useEffect(() => {
    if (isFirstStepRender.current) {
      isFirstStepRender.current = false;
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [step]);

  // Guard: redirect to home for completely unknown category slugs (must be after all hooks)
  if (searchParams.get('category') && !catConfig) {
    return <Navigate to="/" replace />;
  }

  const handleContactChange = (field, value) => {
    setContactForm(prev => ({ ...prev, [field]: value }));
  };

  const validateContactForm = () => {
    if (!contactForm.name?.trim() || !contactForm.phone?.trim()) {
      toast.error("Namn och telefon krävs");
      return false;
    }
    if (!contactForm.email?.trim()) {
      toast.error("E-post krävs (så vi kan skicka offerten)");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email.trim())) {
      toast.error("Ogiltig e-postadress");
      return false;
    }
    if (!contactForm.address_street?.trim()) {
      toast.error("Gatuadress krävs");
      return false;
    }
    const zipDigits = (contactForm.address_zip || "").replace(/\s/g, "");
    if (!/^\d{5}$/.test(zipDigits)) {
      toast.error("Postnummer måste vara 5 siffror (t.ex. 252 34)");
      return false;
    }
    if (!contactForm.address_city?.trim()) {
      toast.error("Ort krävs");
      return false;
    }
    return true;
  };

  const handleSubmitContact = async () => {
    if (!validateContactForm()) return;
    if (submittingContact) return;

    setSubmittingContact(true);
    try {
        const cleanedConfig = cleanData(formData);
        let pdfUrl = '';
        try {
          const { blob, filename } = await buildQuotePdfBlob(categoryFromUrl, result, contactForm);
          const fd = new FormData();
          fd.append('file', blob, filename);
          const up = await apiClient.post('/upload', fd);
          const u = up.data?.url;
          if (u) pdfUrl = resolvePublicUploadUrl(u);
        } catch (pdfErr) {
          console.warn('[wizard] PDF-uppladdning misslyckades:', pdfErr);
        }

        const caseResponse = await apiClient.post('/cases', {
            config: cleanedConfig,
            newsletter_consent: contactForm.newsletter_consent === true,
            contact: {
                name: contactForm.name,
                email: contactForm.email,
                phone: contactForm.phone,
                address_street: contactForm.address_street,
                address_number: contactForm.address_number,
                address_zip: contactForm.address_zip,
                address_city: contactForm.address_city
            },
            photos: formData.photos,
            dimensions: cleanedConfig.dimensions,
            ...(pdfUrl ? { pdf_url: pdfUrl } : {}),
        });
        const caseNumber = caseResponse.data?.case_number;
        setShowContactForm(false);
        setContactPriceRevealed(true);
        setSubmittedCaseNumber(caseNumber ?? null);
    } catch (e) {
        console.error("Booking error:", e);
        const d = e.response?.data;
        const errorDetail =
          (typeof d?.detail === "string" ? d.detail : null) ||
          (typeof d?.error === "string" ? d.error : null) ||
          e.message;
        toast.error(`Kunde inte skicka: ${errorDetail}`);
    } finally {
        setSubmittingContact(false);
    }
  };

  const handleBook = () => {
    setShowContactForm(true);
  };

  const pageTitle = catConfig
    ? `${catConfig.label} | Prisindikation | Klart Hemma`
    : 'Prisindikation | Klart Hemma';
  const modalRotDeduction = formData.customer_type === 'privat'
    ? Number(result?.rot_deduction || 0)
    : 0;
  const modalPrice = Math.max(0, Math.round(Number(result?.price_rec || 0) - modalRotDeduction));
  const modalPriceLow = Math.max(0, Math.round(Number(result?.price_low || 0) - modalRotDeduction));
  const modalPriceHigh = Math.max(0, Math.round(Number(result?.price_high || 0) - modalRotDeduction));

  return (
    <div className={`kh-wizard-shell flex flex-col${isEmbedded ? ' kh-wizard-shell--embedded' : ''}`}>
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={catConfig?.seoDescription || 'Fyll i din förfrågan; efter dina kontaktuppgifter får du en prisindikation, inkl. moms och ROT-avdrag.'} />
        </Helmet>
        {/* Contact Form Dialog */}
        <Dialog open={showContactForm} onOpenChange={setShowContactForm}>
            <DialogContent className="kh-contact-dialog w-[calc(100vw-24px)] max-w-[960px] max-h-[calc(100dvh-24px)] p-0">
              <div className="grid md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
                <aside className="kh-price-summary border-b border-[var(--kh-line)] px-5 py-5 sm:px-7 sm:py-7 md:border-b-0 md:border-r md:px-9 md:py-10">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--kh-evergreen)]">Projektöversikt</p>
                  <h2 className="mt-2 max-w-[16ch] text-xl font-semibold leading-tight tracking-[-0.04em] sm:text-2xl md:mt-3 md:text-4xl">
                    Din prisindikation
                  </h2>
                  <p className="mt-3 hidden max-w-sm text-sm leading-6 text-[var(--kh-muted)] md:block">
                    Kontrollera sammanfattningen innan du skickar offertförfrågan.
                  </p>

                  <div className="mt-3 border-t border-[var(--kh-line)] pt-3 md:mt-8 md:pt-5">
                    <p className="text-xs font-semibold text-[var(--kh-muted)]">ARBETE</p>
                    <p className="mt-1 text-base font-semibold md:text-lg">{catConfig?.label || 'Ditt projekt'}</p>
                    <p className="mt-1 text-xs text-[var(--kh-muted)] md:text-sm">
                      {formData.customer_type === 'privat' ? 'Privatperson' : 'Företag'}
                      {' · '}
                      {formData.price_model === 'fastpris' ? 'Fast pris' : 'Löpande pris'}
                    </p>
                  </div>

                  {result && (
                    <div className="mt-3 rounded-xl border border-[var(--kh-line)] bg-white/70 p-3 md:mt-5 md:p-4">
                      <p className="text-xs font-medium text-[var(--kh-muted)]">{modalRotDeduction > 0 ? 'Pris efter uppskattat ROT-avdrag' : 'Prisindikation'}</p>
                      <p className="mt-1 text-2xl font-semibold tracking-[-0.04em] md:text-3xl">
                        {modalPrice.toLocaleString('sv-SE')} <span className="text-base font-medium text-[var(--kh-muted)]">kr</span>
                      </p>
                      {(modalPriceLow > 0 || modalPriceHigh > 0) && (
                        <p className="mt-1 text-xs text-[var(--kh-muted)]">
                          Prisintervall: {modalPriceLow.toLocaleString('sv-SE')}–{modalPriceHigh.toLocaleString('sv-SE')} kr
                        </p>
                      )}
                    </div>
                  )}

                  {modalRotDeduction > 0 && (
                    <div className="mt-4 text-xs leading-5 text-[var(--kh-muted)]">
                      <p>ROT är uppskattat. Avdraget gäller högst 30 % av godkänd arbetskostnad; material och resor räknas inte in.</p>
                      <a href="https://www.skatteverket.se/skatter/husarbetenrot/saharfungerar.4.5947400c11f47f7f9dd80004014.html" target="_blank" rel="noreferrer" className="mt-1 inline-block text-[var(--kh-evergreen)] underline decoration-[var(--kh-line)] underline-offset-4 hover:text-[var(--kh-evergreen-dark)]">Läs om ROT hos Skatteverket</a>
                    </div>
                  )}
                </aside>

                <div className="px-6 py-8 sm:px-9 sm:py-10">
                  <DialogHeader className="mb-7 space-y-2 text-left">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--kh-evergreen)]">Offertförfrågan</p>
                    <DialogTitle className="text-2xl font-semibold leading-tight tracking-[-0.04em] text-[var(--kh-ink)] sm:text-3xl">
                      Skicka dina kontaktuppgifter
                    </DialogTitle>
                    <DialogDescription className="max-w-md text-sm leading-6 text-[var(--kh-muted)]">
                      Uppgifterna kopplas till projektbeskrivningen och prisindikation.
                    </DialogDescription>
                  </DialogHeader>

                  <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="quote-contact-name" className="text-xs font-semibold text-[var(--kh-ink)]">Namn *</Label>
                      <Input id="quote-contact-name" autoComplete="name" placeholder="För- och efternamn" value={contactForm.name} onChange={(e) => handleContactChange('name', e.target.value)} className="mt-1.5 h-11 rounded-lg border-[var(--kh-line)] bg-[var(--kh-surface)]" />
                    </div>
                    <div>
                      <Label htmlFor="quote-contact-phone" className="text-xs font-semibold text-[var(--kh-ink)]">Telefon *</Label>
                      <Input id="quote-contact-phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="070-123 45 67" value={contactForm.phone} onChange={(e) => handleContactChange('phone', e.target.value)} className="mt-1.5 h-11 rounded-lg border-[var(--kh-line)] bg-[var(--kh-surface)]" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="quote-contact-email" className="text-xs font-semibold text-[var(--kh-ink)]">E-post *</Label>
                      <Input id="quote-contact-email" type="email" autoComplete="email" placeholder="din@email.se" value={contactForm.email} onChange={(e) => handleContactChange('email', e.target.value)} className="mt-1.5 h-11 rounded-lg border-[var(--kh-line)] bg-[var(--kh-surface)]" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="quote-contact-street" className="text-xs font-semibold text-[var(--kh-ink)]">Gatuadress *</Label>
                      <Input id="quote-contact-street" autoComplete="street-address" placeholder="Gatuadress" value={contactForm.address_street} onChange={(e) => handleContactChange('address_street', e.target.value)} className="mt-1.5 h-11 rounded-lg border-[var(--kh-line)] bg-[var(--kh-surface)]" />
                    </div>
                    <div>
                      <Label htmlFor="quote-contact-zip" className="text-xs font-semibold text-[var(--kh-ink)]">Postnummer *</Label>
                      <Input id="quote-contact-zip" inputMode="numeric" autoComplete="postal-code" placeholder="252 34" value={contactForm.address_zip} onChange={(e) => handleContactChange('address_zip', e.target.value)} className="mt-1.5 h-11 rounded-lg border-[var(--kh-line)] bg-[var(--kh-surface)]" />
                    </div>
                    <div>
                      <Label htmlFor="quote-contact-city" className="text-xs font-semibold text-[var(--kh-ink)]">Ort *</Label>
                      <Input id="quote-contact-city" autoComplete="address-level2" placeholder="Ort" value={contactForm.address_city} onChange={(e) => handleContactChange('address_city', e.target.value)} className="mt-1.5 h-11 rounded-lg border-[var(--kh-line)] bg-[var(--kh-surface)]" />
                    </div>
                  </div>

                  <div className="mt-5 flex items-start gap-3 rounded-xl border border-[var(--kh-line)] bg-[var(--kh-paper)] p-4">
                    <Checkbox id="quote-newsletter-consent" checked={contactForm.newsletter_consent} onCheckedChange={(checked) => handleContactChange('newsletter_consent', checked === true)} className="mt-0.5" />
                    <Label htmlFor="quote-newsletter-consent" className="cursor-pointer text-sm font-normal leading-6 text-[var(--kh-ink)]">
                      Ja, jag vill få nyheter och erbjudanden från Klart Hemma via e-post. Jag kan avsluta utskicken när som helst.
                    </Label>
                  </div>

                  <p className="mt-4 text-xs leading-5 text-[var(--kh-muted)]">
                    Vi använder dina uppgifter för att svara på förfrågan. <a href="/integritet" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-[var(--kh-ink)]">Läs integritetspolicyn</a>.
                  </p>

                  <DialogFooter className="mt-7 flex w-full flex-col gap-2 border-t border-slate-200 pt-5 sm:flex-col sm:items-stretch sm:space-x-0">
                    <Button type="button" onClick={handleSubmitContact} className="kh-button w-full min-h-12 px-6 text-sm" disabled={submittingContact}>
                      {submittingContact ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Skickar...</> : 'Skicka förfrågan'}
                      {!submittingContact && <ArrowRight className="ml-2 h-4 w-4" />}
                    </Button>
                    <Button type="button" variant="ghost" className="w-full text-[var(--kh-muted)] hover:bg-[var(--kh-paper)] hover:text-[var(--kh-ink)]" onClick={() => setShowContactForm(false)} disabled={submittingContact}>
                      Avbryt
                    </Button>
                  </DialogFooter>
                </div>
              </div>
            </DialogContent>
        </Dialog>

        {!isEmbedded && <AppMenu />}

        <div className="w-full max-w-3xl mx-auto px-4 pt-4 mb-2 flex justify-end items-center gap-3">
             <a href="tel:0707388602" className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-primary transition-colors">
                 <Phone className="h-3.5 w-3.5" />
                 <span className="hidden sm:inline">Behöver du hjälp?</span>
             </a>
             <Button variant="ghost" size="sm" onClick={() => setShowSaveDialog(true)} title="Spara utkast" className="h-8 px-2 text-slate-500 hover:text-primary">
                 <Save className="h-4 w-4 mr-1" /> <span className="hidden sm:inline text-xs">Spara</span>
             </Button>
        </div>

        {/* Save Dialog */}
        <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Spara utkast</DialogTitle>
                    <DialogDescription>Utkastet sparas på servern. Vi kopierar en länk så du kan fortsätta senare.</DialogDescription>
                </DialogHeader>
                <div className="py-4">
                    <Label>E-post</Label>
                    <Input value={saveEmail} onChange={(e) => setSaveEmail(e.target.value)} placeholder="din@email.se" />
                </div>
                <DialogFooter>
                    <Button onClick={handleSaveDraft}><Mail className="mr-2 h-4 w-4" /> Kopiera länk</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        {draftLoaded && (
            <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-4 fade-in duration-500">
                <div className="bg-accent-light border border-accent/30 text-primary px-4 py-3 rounded-md shadow-lg flex items-center gap-3 max-w-sm">
                    <Info className="h-5 w-5 text-accent flex-shrink-0" />
                    <div>
                        <p className="font-bold text-sm">Vi har fyllt i dina snabbval!</p>
                        <p className="text-xs opacity-90">Du kan ändra dem när som helst.</p>
                    </div>
                    <button type="button" className="ml-auto h-6 w-6 flex items-center justify-center rounded hover:bg-accent/30 text-primary/60 hover:text-primary transition-colors text-sm" onClick={() => setDraftLoaded(false)}>✕</button>
                </div>
            </div>
        )}

        <div className="flex-1 w-full max-w-3xl mx-auto px-4 pb-16">
            <p className="text-center text-xs sm:text-sm font-semibold text-accent-dark mb-3 px-1">ROT kan ge upp till 30 % av arbetskostnaden</p>
            {/* Category label + step indicator */}
            {step < 4 && (() => {
                const stepNames = ['', 'Kom igång', isKitchen ? 'Köksbeskrivning' : 'Beskriv arbetet', 'Bilder & Mått'];
                return (
                    <div className="flex justify-between items-center mb-3">
                        <div className="flex items-center gap-2">
                          {catConfig && <CatIcon name={catConfig.icon} className="h-4 w-4 text-[#374ef4] shrink-0" />}
                          <span className="text-xs font-semibold text-accent-dark uppercase tracking-wider">
                              {stepNames[step]}
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground">{step} / 3</span>
                    </div>
                );
            })()}

            <div className="kh-wizard-card min-h-[400px]">
                {/* Progress stripe */}
                <div className="h-1 bg-[#d8d6cc]">
                    <div className="h-full bg-[#374ef4] transition-all duration-500" style={{ width: `${Math.min((step / 3) * 100, 100)}%` }} />
                </div>
                <div className="p-6 md:p-10">
                    {step === 1 && <Step1_Basics data={formData} update={update} next={next} goHome={goHome} />}
                    {step === 2 && (
                      <div className="space-y-8">
                        {isKitchen ? (
                          <>
                            <Step3_Config data={formData} update={update} next={next} back={back} errors={errors} hideNav />
                            <hr className="border-slate-200" />
                            <Step4_Addons data={formData} update={update} next={next} back={back} hideNav />
                          </>
                        ) : (
                          <>
                            {/* VVS: photos collected here (no dimensions step) */}
                            {categoryFromUrl === 'vvs' && (
                              <>
                                <Step5_Photos data={formData} update={update} next={next} back={back} errors={errors} hideNav />
                                <hr className="border-slate-200" />
                              </>
                            )}
                            <CategoryScopeStep category={categoryFromUrl} data={formData} update={update} next={next} back={back} errors={errors} hideNav />
                            {categoryFromUrl !== 'vvs' && <><hr className="border-slate-200" /><CategoryAddonsStep category={categoryFromUrl} data={formData} update={update} next={next} back={back} hideNav /></>}
                          </>
                        )}
                        <div className="flex flex-col sm:flex-row gap-3 sm:justify-between mt-6">
                          <Button variant="outline" onClick={back} className="w-full sm:w-auto"><ArrowLeft className="mr-2 h-4 w-4" /> Tillbaka</Button>
                          <div className="flex flex-col items-stretch sm:items-end gap-1 w-full sm:w-auto">
                            <Button onClick={next} className="w-full sm:w-auto bg-[#374ef4] hover:bg-[#2a3fd4] text-white font-bold">
                              {categoryFromUrl === 'vvs' ? 'Visa prisindikation' : 'Nästa'} <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                            {categoryFromUrl === 'vvs' && <span className="text-[10px] text-muted-foreground text-center sm:text-right">ROT enligt gällande villkor</span>}
                          </div>
                        </div>
                      </div>
                    )}
                    {step === 3 && (
                      <div className="space-y-8">
                        <Step5_Photos data={formData} update={update} next={next} back={back} errors={errors} hideNav />
                        {/* Dimensions only shown for categories where room measurements matter */}
                        {categoryFromUrl !== 'vvs' && categoryFromUrl !== 'el' && (
                          <>
                            <hr className="border-slate-200" />
                            <Step6_Dimensions data={formData} update={update} next={next} back={back} errors={errors} hideNav />
                          </>
                        )}
                        <div className="flex flex-col sm:flex-row gap-3 sm:justify-between mt-6 sm:items-end">
                          <Button variant="outline" onClick={back} className="w-full sm:w-auto"><ArrowLeft className="mr-2 h-4 w-4" /> Tillbaka</Button>
                          <div className="flex flex-col items-stretch sm:items-end gap-1 w-full sm:w-auto">
                            <Button onClick={next} variant="default" className="w-full sm:w-auto bg-[#374ef4] hover:bg-[#2a3fd4] text-white font-bold px-8">Visa prisindikation</Button>
                            <span className="text-[10px] text-muted-foreground text-center sm:text-right">ROT enligt gällande villkor</span>
                          </div>
                        </div>
                      </div>
                    )}
                    {step === 4 && (
                        <Step7_Result
                            data={formData}
                            result={result}
                            onBook={handleBook}
                            back={back}
                            goToStep={setStep}
                            category={categoryFromUrl}
                            contactForm={contactForm}
                            priceRevealed={contactPriceRevealed}
                            submittedCaseNumber={submittedCaseNumber}
                            onContinueToThanks={() =>
                              navigate('/tack', {
                                replace: true,
                                state: submittedCaseNumber != null ? { caseNumber: submittedCaseNumber } : undefined,
                              })
                            }
                        />
                    )}
                </div>
            </div>
        </div>

        {!isEmbedded && <AppFooter />}
    </div>
  );
}
